import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, IconButton, Typography, Box,
  MenuItem, Select, FormControl, InputLabel, FormHelperText,
  CircularProgress, alpha, Stack, Chip, Avatar,
  Switch, Snackbar, Alert, InputAdornment, Autocomplete,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import EditNoteIcon from '@mui/icons-material/EditNote';
import LinkIcon from '@mui/icons-material/Link';
import TuneIcon from '@mui/icons-material/Tune';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LanguageIcon from '@mui/icons-material/Language';
import ImageIcon from '@mui/icons-material/Image';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ClearIcon from '@mui/icons-material/Clear';
import { fetchRecords, fetchFfDefaults, fetchApprovers, fetchForm16Defaults, fetchNextEmployeeCode } from '../services/api';
import { styled } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FFRecoveryPanel from './FFRecoveryPanel';
import { useApp } from '../context/AppContext';
import { tableLabel } from '../config/tableLabels';
import { TABLE_CONFIG, generatePK } from '../config/fieldConfig';
import { COUNTRIES, getCountryData, getStates, getCities, getCurrencyByCountry } from '../config/geoData';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const NUM_SUFFIXES = [
  'amount', 'basic', 'hra', 'da', 'gross', 'ctc', 'salary', 'balance', 'priority', 'percentage',
  'rate', 'hours', 'days', 'count', 'value', 'cutoff_day', 'payment_day', 'payment_date_offset', 'number_of_years',
  'processing_priority', 'display_sequence', 'working_days', 'period_number', 'loan_amount',
  'interest_rate', 'emi_amount', 'tenure_months', 'total_recovered', 'outstanding', 'ot_hours',
  'late_minutes', 'early_exit_minutes', 'actual_hours', 'opening_balance', 'accrued', 'availed',
  'encashed', 'lapsed', 'closing_balance', 'declared_amount', 'approved_amount', 'hours_per_day',
  'days_per_week', 'working_days_per_month', 'break_minutes', 'ot_threshold_daily',
  'ot_threshold_weekly', 'ot_multiplier', 'annual_entitlement', 'max_carry_forward_days',
  'percentage_value', 'max_ceiling', 'tds_monthly', 'monthly_pf_ee', 'monthly_pf_er',
  'monthly_eps_er', 'ytd_pf_ee', 'split_pct', 'gross_earnings', 'total_deductions', 'net_payable',
  'ytd_gross', 'ytd_tds', 'total_employees', 'total_gross', 'total_net', 'error_count', 'costing_amount',
];
const isLegacyNum = k => NUM_SUFFIXES.some(s => k.toLowerCase() === s || k.toLowerCase().endsWith('_' + s));
const isLegacyDate = k => k.includes('date') || k.endsWith('_from') || k.endsWith('_to');
const isLegacyEmail = k => ['email', 'contact_email', 'emp_email'].some(e => k.includes(e));
const toLabel = key => key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const SECTION_CONFIG = {
  // Auto-categorized fallback sections
  references: { icon: <LinkIcon sx={{ fontSize: 14 }} />, color: '#22c55e', bg: '#f0fdf4' },
  details: { icon: <TuneIcon sx={{ fontSize: 14 }} />, color: '#059669', bg: '#ecfdf5' },
  dates: { icon: <DateRangeIcon sx={{ fontSize: 14 }} />, color: '#059669', bg: '#dcfce7' },
  status: { icon: <ToggleOnIcon sx={{ fontSize: 14 }} />, color: '#D97706', bg: '#FEF9C3' },
  // Named sections (from tableConfig.sections)
  basic: { icon: <EditNoteIcon sx={{ fontSize: 14 }} />, color: '#22c55e', bg: '#f0fdf4' },
  regional: { icon: <LanguageIcon sx={{ fontSize: 14 }} />, color: '#059669', bg: '#ecfdf5' },
  financial: { icon: <DateRangeIcon sx={{ fontSize: 14 }} />, color: '#059669', bg: '#ecfdf5' },
  compliance: { icon: <TuneIcon sx={{ fontSize: 14 }} />, color: '#059669', bg: '#dcfce7' },
  media: { icon: <ImageIcon sx={{ fontSize: 14 }} />, color: '#16a34a', bg: '#f0fdf4' },
  metadata: { icon: <ToggleOnIcon sx={{ fontSize: 14 }} />, color: '#D97706', bg: '#FEF9C3' },
};

// Common Autocomplete TextField props to keep renderers DRY
const autoTF = (params, { label, req, err, hint, hintStyle }) => ({
  ...params,
  size: 'small',
  label,
  required: req,
  error: !!err,
  helperText: err || hint || '\u00A0',
  InputLabelProps: { sx: { fontSize: 13 } },
  InputProps: { ...params.InputProps, sx: { fontSize: 13, borderRadius: '8px' } },
  FormHelperTextProps: { sx: { fontSize: 11, ...(hintStyle || {}) } },
});

export default function RecordForm({ open, onClose, onSave, schema, record, tableName, existingRecords = [] }) {
  const { state: appState } = useApp();
  const activeContext = appState?.activeContext || null;
  const tableConfig = TABLE_CONFIG[tableName] || null;

  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [ddOptions, setDdOptions] = useState({});
  const [ddLoading, setDdLoading] = useState({});
  const [cascadeRawData, setCascadeRawData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [approverList, setApproverList] = useState([]);

  const isFormLocked = tableName === 'FULL_FINAL_SETTLEMENT' && (formData.status === 'APPROVED' || formData.status === 'REJECTED');

  // Load valid approvers for the request if in ADVANCE_SALARY_REQUEST or ADVANCE_SALARY mode
  useEffect(() => {
    if (open && (tableName === 'ADVANCE_SALARY_REQUEST' || tableName === 'ADVANCE_SALARY') && record) {
      const reqId = record.isRequest ? (record.id || record.request_id) : record.loan_request_id;
      if (reqId) {
        fetchApprovers(reqId)
          .then(res => {
            setApproverList(res.data?.data || []);
          })
          .catch(err => {
            console.error('Error loading approvers in RecordForm:', err);
          });
      }
    } else {
      setApproverList([]);
    }
  }, [open, tableName, record]);

  // ── Load FK dropdown data ─────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !tableConfig) return;
    const dropdownFields = Object.entries(tableConfig.fields).filter(([, cfg]) => cfg.type === 'dropdown' || cfg.type === 'multi-dropdown');
    if (dropdownFields.length) {
      const loading = {};
      dropdownFields.forEach(([f]) => { loading[f] = true; });
      setDdLoading(loading);
      dropdownFields.forEach(([field, cfg]) => {
        const queryParams = { lookup: true, ...(cfg.apiParams || {}) };
        if (cfg.includeInactive) queryParams.includeInactive = 'true';

        fetchRecords(cfg.source, queryParams)
          .then(res => {
            let rows = res.data.data || [];
            if (typeof cfg.filterFn === 'function') {
              rows = rows.filter(r => cfg.filterFn(r, existingRecords, record));
            }
            let options = rows.map(row => {
              const label = cfg.labelCombo
                ? cfg.labelCombo.map(f => row[f]).filter(Boolean).join(' - ')
                : (row[cfg.labelField] || row[cfg.valueField]);
              // Stash raw row so cascading dropdowns can filter by parent field
              return { value: row[cfg.valueField], label, _row: row };
            });

            // STRICT FILTERING FOR APPROVED_BY & REJECTED_BY IN FORM
            if ((tableName === 'ADVANCE_SALARY_REQUEST' || tableName === 'ADVANCE_SALARY') && (field === 'approved_by' || field === 'rejected_by') && approverList.length > 0) {
              const validIds = new Set(approverList.map(a => a.id));
              if (record && record[field]) validIds.add(record[field]);
              options = options.filter(opt => validIds.has(opt.value));
            }

            setDdOptions(prev => ({ ...prev, [field]: options }));

            // Auto-select the lone option when there's exactly one and
            // the field opted in via `autoSelectSingle: true`.
            // Skip for multi-dropdown (handled separately) and only when current is empty.
            if (options.length === 1 && cfg.autoSelectSingle && cfg.type === 'dropdown') {
              setFormData(prev => {
                const cur = prev[field];
                if (cur !== undefined && cur !== '' && cur !== null) return prev;
                return { ...prev, [field]: options[0].value };
              });
            }
          })
          .catch(() => setDdOptions(prev => ({ ...prev, [field]: [] })))
          .finally(() => setDdLoading(prev => ({ ...prev, [field]: false })));
      });
    }
    // ── Load cascade-select source tables (store raw records for filtering) ─
    const cascadeFields = Object.entries(tableConfig.fields).filter(([, cfg]) => cfg.type === 'cascade-select');
    const uniqueSources = [...new Set(cascadeFields.map(([, cfg]) => cfg.source))];
    uniqueSources.forEach(source => {
      fetchRecords(source)
        .then(res => setCascadeRawData(prev => ({ ...prev, [source]: res.data.data || [] })))
        .catch(() => setCascadeRawData(prev => ({ ...prev, [source]: [] })));
    });
  }, [open, tableName, approverList]);

  // ── Initialise form data ──────────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      setIsSubmitting(false);
      initAppliedRef.current = null;
      salaryInitAppliedRef.current = null;
      setFormData({});
      setDdOptions({});
      setDdLoading({});
      setCascadeRawData({});
      return;
    }
    setErrors({});
    if (record) { setFormData({ ...record }); return; }

    const data = {};
    schema.forEach(field => {
      if (field === '_id') return;
      if (!tableConfig) { data[field] = ''; return; }
      const cfg = tableConfig.fields[field];
      if (!cfg) { data[field] = ''; return; }
      if (cfg.type === 'auto') { data[field] = cfg.autoValue || 'SYSTEM'; return; }
      if (cfg.type === 'flag') { data[field] = 'Y'; return; }
      // Honor `defaultValue` for new records (e.g. currency='INR')
      data[field] = (cfg.defaultValue !== undefined && cfg.defaultValue !== null) ? cfg.defaultValue : '';
    });
    // Also apply defaultValue to fields declared in fieldConfig but not in schema (newer tables)
    if (tableConfig?.fields) {
      Object.entries(tableConfig.fields).forEach(([f, cfg]) => {
        if (f in data) return;
        if (cfg?.defaultValue !== undefined && cfg?.defaultValue !== null) {
          data[f] = cfg.defaultValue;
        }
      });
    }
    // Extra fields defined in sections but absent from backend schema (e.g. image uploads)
    if (tableConfig?.sections) {
      tableConfig.sections.forEach(section => {
        section.fields.forEach(f => {
          if (f in data) return;
          const cfg = tableConfig.fields[f];
          if (!cfg || cfg.type === 'auto' || f === tableConfig.pk) return;
          data[f] = cfg.type === 'flag' ? 'Y' : '';
        });
      });
    }

    // Auto-fill COMPANY_id / bg_id / ldg_id from active payroll context
    // for new records (skip when editing — preserve original values).
    // Skip on COMPANY table itself to avoid self-reference.
    if (activeContext && tableName !== 'COMPANY') {
      const mapPairs = [
        ['COMPANY_id', activeContext.company_id],
        ['company_id', activeContext.company_id],
        ['bg_id', activeContext.bg_id],
        ['ldg_id', activeContext.ldg_id],
      ];
      // Force-fill when the field is part of the schema/sections OR present in fieldConfig
      const knownFields = new Set([
        ...schema,
        ...(tableConfig ? Object.keys(tableConfig.fields || {}) : []),
        ...(tableConfig?.sections ? tableConfig.sections.flatMap(s => s.fields) : []),
      ]);
      for (const [f, v] of mapPairs) {
        if (v && knownFields.has(f)) {
          data[f] = v;
        }
      }
    }

    // Apply `mirrorField` for initial values too — when a field already has
    // a value (e.g. from activeContext), copy it into any sibling that mirrors
    // it but is currently empty. Otherwise mirrorField only fires on user
    // changes, missing context-prefilled cases.
    if (tableConfig?.fields) {
      Object.entries(tableConfig.fields).forEach(([f, c]) => {
        if (c?.mirrorField && !data[f]) {
          const src = data[c.mirrorField];
          if (src) data[f] = src;
        }
      });
    }

    setFormData(data);
  }, [open, record, schema, tableName, activeContext]);

  // Watch company / ldg to auto-fill currency
  useEffect(() => {
    if (!open || !tableConfig || !ddOptions || !formData) return;
    const hasCurrencyField = tableConfig.fields && ('currency' in tableConfig.fields || 'currency_code' in tableConfig.fields);
    if (!hasCurrencyField) return;

    const currentCurrency = formData.currency || formData.currency_code;
    if (currentCurrency) return;

    const companyId = formData.COMPANY_id || formData.company_id;
    const ldgId = formData.ldg_id;

    // Try to get currency code from ldg_id first
    if (ldgId && ddOptions['ldg_id']) {
      const ldgRow = ddOptions['ldg_id'].find(opt => opt.value === ldgId)?._row;
      if (ldgRow?.currency_code) {
        setFormData(prev => {
          const field = 'currency' in tableConfig.fields ? 'currency' : 'currency_code';
          if (prev[field]) return prev;
          return { ...prev, [field]: ldgRow.currency_code };
        });
        return;
      }
    }

    // Fallback to company_id -> default_ldg_id -> currency_code
    if (companyId && (ddOptions['COMPANY_id'] || ddOptions['company_id']) && ddOptions['ldg_id']) {
      const companyOptions = ddOptions['COMPANY_id'] || ddOptions['company_id'] || [];
      const companyRow = companyOptions.find(opt => opt.value === companyId)?._row;
      if (companyRow?.default_ldg_id) {
        const ldgRow = ddOptions['ldg_id'].find(opt => opt.value === companyRow.default_ldg_id)?._row;
        if (ldgRow?.currency_code) {
          setFormData(prev => {
            const field = 'currency' in tableConfig.fields ? 'currency' : 'currency_code';
            if (prev[field]) return prev;
            return { ...prev, [field]: ldgRow.currency_code };
          });
        }
      }
    }
  }, [open, formData?.COMPANY_id, formData?.company_id, formData?.ldg_id, ddOptions, tableConfig, tableName]);

  // When editing an existing record, wait for dropdown options and cascade data
  // to finish loading before populating the form. This ensures dependent
  // dropdowns (e.g. bg_id -> COMPANY_id -> ldg_id -> assignment_id) have
  // their options available so the selected values render correctly.
  const initAppliedRef = React.useRef(null);
  useEffect(() => {
    if (!open || !record || !tableConfig) return;

    // Dropdown fields defined for this table
    const dropdownFields = Object.entries(tableConfig.fields || {})
      .filter(([, cfg]) => cfg.type === 'dropdown' || cfg.type === 'multi-dropdown')
      .map(([f]) => f);

    // If any dropdown is still loading or hasn't loaded yet, wait
    const anyDdLoading = dropdownFields.some(f => ddLoading[f] || ddOptions[f] === undefined);
    if (anyDdLoading) return;

    // Ensure cascade-select source data has been loaded too
    const cascadeFields = Object.entries(tableConfig.fields || {}).filter(([, cfg]) => cfg.type === 'cascade-select');
    const cascadeSources = [...new Set(cascadeFields.map(([, cfg]) => cfg.source))];
    const missingCascade = cascadeSources.some(s => cascadeRawData[s] === undefined);
    if (missingCascade) return;

    const pk = tableConfig.pk;
    // Only initialize once per record open to avoid clobbering user edits
    if (record[pk] && initAppliedRef.current === String(record[pk])) return;

    // Build normalized data where dropdown values use the exact option.value type
    const normalized = { ...record };
    dropdownFields.forEach(f => {
      const val = record[f];
      if (val === undefined || val === null || val === '') return;
      const options = ddOptions[f] || [];
      if (Array.isArray(val)) {
        // multi-dropdown: map to option.value instances
        const mapped = val.map(v => {
          const found = options.find(o => String(o.value) === String(v));
          return found ? found.value : v;
        });
        normalized[f] = mapped;
      } else {
        const found = options.find(o => String(o.value) === String(val));
        if (found) normalized[f] = found.value;
        else normalized[f] = val;
      }
    });

    // --- Apply autoFillFromFk for existing records ---
    const applyAutoFill = (srcField, srcValue) => {
      const sCfg = tableConfig.fields[srcField];
      if (!sCfg?.autoFillFromFk) return;
      const opts = ddOptions[srcField] || (sCfg.source ? (cascadeRawData[sCfg.source] || []).map(r => ({ value: r[sCfg.valueField], _row: r })) : []);
      const row = opts.find(o => String(o.value) === String(srcValue))?._row;
      if (row) {
        Object.entries(sCfg.autoFillFromFk).forEach(([target, readField]) => {
          const v = row[readField];
          if (v !== undefined && v !== null) {
            // Only auto-fill if the existing record lacks a value so we don't overwrite manual edits
            if (record[target] === undefined || record[target] === null || record[target] === '') {
              normalized[target] = v;
              applyAutoFill(target, v);
            }
          }
        });
      }
    };
    Object.keys(tableConfig.fields || {}).forEach(f => {
      if (normalized[f]) applyAutoFill(f, normalized[f]);
    });

    // Also normalize flag fields (Boolean -> 'Y'/'N')
    const flagFields = Object.entries(tableConfig.fields || {})
      .filter(([, cfg]) => cfg.type === 'flag')
      .map(([f]) => f);

    flagFields.forEach(f => {
      if (record[f] === true) normalized[f] = 'Y';
      if (record[f] === false) normalized[f] = 'N';
    });

    // --- PF & ESI ACCOUNT Custom Init Logic ---
    if (normalized.assignment_id && (tableName === 'PF_ACCOUNT' || tableName === 'ESI_ACCOUNT')) {
      const asnOpt = (ddOptions['assignment_id'] || []).find(o => String(o.value) === String(normalized.assignment_id));
      const row = asnOpt?._row;
      if (row) {
        if (tableName === 'PF_ACCOUNT') {
          if (row.company?.statutory_configurations) {
            const pfConfig = row.company.statutory_configurations.find(c => c.component_code === 'PF' || c.component_code === 'EPF');
            if (pfConfig?.configuration_json) {
              const config = pfConfig.configuration_json;
              normalized.pf_estab_code = config.estab_code || config.epf_establishment_code || normalized.pf_estab_code || '';
              let parsedOffice = '';
              if (config.epf_establishment_code && typeof config.epf_establishment_code === 'string') {
                const parts = config.epf_establishment_code.split('/');
                if (parts.length >= 2) parsedOffice = parts[1];
              }
              normalized.pf_office_code = config.office_code || parsedOffice || normalized.pf_office_code || '';
            }
          }
          if (row.default_template) {
            const els = row.default_template.elements || [];
            const basicEl = els.find(e => e.element?.element_name?.toLowerCase().includes('basic'));
            if (basicEl && typeof basicEl.element_value === 'number') {
              if (normalized.basic_monthly === undefined || normalized.basic_monthly === null || normalized.basic_monthly === '') {
                normalized.basic_monthly = basicEl.element_value;
              }
            }
          }
        } else if (tableName === 'ESI_ACCOUNT') {
          if (row.company?.statutory_configurations) {
            const esiConfig = row.company.statutory_configurations.find(c => c.component_code === 'ESI');
            if (esiConfig?.configuration_json) {
              const config = esiConfig.configuration_json;
              if (normalized.gross_ceiling === undefined || normalized.gross_ceiling === null || normalized.gross_ceiling === '') {
                normalized.gross_ceiling = config.esi_wage_ceiling !== undefined ? config.esi_wage_ceiling : 21000;
              }
              normalized.esi_estab_code = config.esi_employer_code || normalized.esi_estab_code || '';
            }
          }
        }
      }
    }

    // Run compute for any fields with recomputeOnChange
    if (tableConfig && tableConfig.fields) {
      Object.entries(tableConfig.fields).forEach(([f, c]) => {
        if (c?.recomputeOnChange && typeof c.compute === 'function') {
          const computedVal = c.compute(normalized);
          if (computedVal !== undefined) normalized[f] = computedVal;
        }
      });
    }

    setFormData(normalized);
    initAppliedRef.current = record[pk] ? String(record[pk]) : null;
  }, [open, record, tableConfig, ddLoading, cascadeRawData, ddOptions]);

  // ADVANCE_SALARY: Auto-fill recovery dates and disbursement dates on initial load or record load
  const salaryInitAppliedRef = React.useRef(null);
  useEffect(() => {
    if (!open || tableName !== 'ADVANCE_SALARY' || !formData.assignment_id) return;

    // Check if ddOptions are fully loaded
    const periodOpts = ddOptions['first_period_id'] || [];
    const assignmentOpts = ddOptions['assignment_id'] || [];
    if (periodOpts.length === 0 || assignmentOpts.length === 0) return;

    // Run this logic only once per record edit session
    const recordKey = record ? (record.id || record.loan_id || 'new') : 'new';
    if (salaryInitAppliedRef.current === recordKey) return;
    salaryInitAppliedRef.current = recordKey;

    setFormData(prev => {
      const updates = {};
      const numInstallments = parseInt(prev.number_of_installments, 10);
      const tenure = parseInt(prev.tenure_months, 10);

      // Get employee's payroll and sorted pay periods
      let payrollId = null;
      let sortedPeriods = [];
      const assignmentRow = assignmentOpts.find(opt => opt.value === prev.assignment_id)?._row;
      if (assignmentRow && assignmentRow.payroll_id) {
        payrollId = assignmentRow.payroll_id;
        const employeePeriods = periodOpts.filter(opt => opt._row && String(opt._row.payroll_id) === String(payrollId)) || [];
        sortedPeriods = [...employeePeriods].sort((a, b) => (a._row.start_date || '').localeCompare(b._row.start_date || ''));
      }

      let resolvedFirstPeriodId = prev.first_period_id;
      if (!resolvedFirstPeriodId && sortedPeriods.length > 0) {
        const baseDate = prev.disbursement_date || prev.payment_date || prev.approval_date || new Date().toISOString().split('T')[0];
        const firstPeriodOpt = sortedPeriods.find(opt => (opt._row.end_date || '') >= baseDate) || sortedPeriods[0];
        if (firstPeriodOpt) {
          resolvedFirstPeriodId = firstPeriodOpt.value;
          updates.first_period_id = resolvedFirstPeriodId;
        }
      }

      if (resolvedFirstPeriodId && sortedPeriods.length > 0) {
        const firstPeriodOpt = periodOpts.find(opt => opt.value === resolvedFirstPeriodId);
        const selectedPeriodRow = firstPeriodOpt?._row;

        if (selectedPeriodRow) {
          if (!prev.recovery_start_date) {
            updates.recovery_start_date = selectedPeriodRow.start_date || '';
          }

          const N = numInstallments || 1;

          let completionDate = prev.recovery_completion_date;
          let lastDeductionDate = prev.last_deduction_date;

          if (!completionDate || !lastDeductionDate) {
            const firstIdx = sortedPeriods.findIndex(opt => opt.value === resolvedFirstPeriodId);
            if (firstIdx !== -1 && sortedPeriods[firstIdx + N - 1]) {
              const finalPeriodRow = sortedPeriods[firstIdx + N - 1]._row;
              if (!completionDate) completionDate = finalPeriodRow.end_date || '';
              if (!lastDeductionDate) lastDeductionDate = finalPeriodRow.payment_date || finalPeriodRow.end_date || '';
            }
          }

          const addMonths = (dateStr, months) => {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return '';
            d.setMonth(d.getMonth() + months);
            return d.toISOString().split('T')[0];
          };

          if (!completionDate && selectedPeriodRow.end_date) {
            completionDate = addMonths(selectedPeriodRow.end_date, N - 1);
          }
          if (!lastDeductionDate && (selectedPeriodRow.payment_date || selectedPeriodRow.end_date)) {
            lastDeductionDate = addMonths(selectedPeriodRow.payment_date || selectedPeriodRow.end_date, N - 1);
          }

          if (!prev.recovery_completion_date) updates.recovery_completion_date = completionDate;
          if (!prev.last_deduction_date) updates.last_deduction_date = lastDeductionDate;

          const addDays = (dateStr, days) => {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return '';
            d.setDate(d.getDate() + days);
            return d.toISOString().split('T')[0];
          };

          if (!prev.loan_closure_date && lastDeductionDate && prev.loan_status === 'CLOSED') {
            updates.loan_closure_date = addDays(lastDeductionDate, 1);
          }
        }
      }

      // Auto-fill Disbursement Date and Payment Date if empty
      const approvalDate = prev.approval_date || prev.disbursement_date || prev.payment_date || new Date().toISOString().split('T')[0];
      if (!prev.disbursement_date) updates.disbursement_date = approvalDate;

      // Once disbursed, the loan status becomes Active (only for new records)
      const isExistingRecord = record && (record.id || record.loan_id);
      const resolvedDisbDate = prev.disbursement_date || updates.disbursement_date;
      if (!isExistingRecord && resolvedDisbDate && (!prev.loan_status || ['PENDING', 'APPROVED', 'DISBURSED'].includes(prev.loan_status))) {
        updates.loan_status = 'ACTIVE';
      }

      if (!prev.payment_date && sortedPeriods.length > 0) {
        const checkDate = prev.disbursement_date || approvalDate;
        const currentPeriodOpt = sortedPeriods.find(opt => (opt._row.start_date || '') <= checkDate && (opt._row.end_date || '') >= checkDate)
          || sortedPeriods.find(opt => (opt._row.end_date || '') >= checkDate)
          || sortedPeriods[0];
        if (currentPeriodOpt?._row) {
          updates.payment_date = currentPeriodOpt._row.payment_date || currentPeriodOpt._row.end_date || '';
        }
      }

      if (Object.keys(updates).length > 0) {
        return { ...prev, ...updates };
      }
      return prev;
    });
  }, [open, tableName, formData.assignment_id, ddOptions, record]);

  // FULL_FINAL_SETTLEMENT: Auto-fetch defaults on edit mount if totals or dates are missing
  useEffect(() => {
    if (!open || tableName !== 'FULL_FINAL_SETTLEMENT' || !formData.assignment_id) return;
    const needsFetch = (!formData.gross_settlement || !formData.net_payable) || 
                       (formData.notice_period_days === undefined || formData.notice_period_days === '' || formData.notice_period_days === null) ||
                       (!formData.notice_start_date);
                       
    if (record?.id && needsFetch) {
      fetchFfDefaults(formData.assignment_id)
        .then(res => {
          if (res.data?.success && res.data?.data) {
            const defaults = res.data.data;
            setFormData(prev => ({
              ...prev,
              gross_settlement: prev.gross_settlement ?? defaults.gross_settlement ?? '',
              total_earnings: prev.total_earnings ?? defaults.total_earnings ?? '',
              total_deductions: prev.total_deductions ?? defaults.total_deductions ?? '',
              total_recovery: prev.total_recovery ?? defaults.total_recovery ?? '',
              net_payable: prev.net_payable ?? defaults.net_payable ?? '',
              pf_deduction: prev.pf_deduction ?? defaults.pf_deduction ?? 0,
              esi_deduction: prev.esi_deduction ?? defaults.esi_deduction ?? 0,
              notice_period_days: prev.notice_period_days || defaults.notice_period_days || 0,
              notice_start_date: prev.notice_start_date || defaults.notice_start_date || '',
              resignation_date: prev.resignation_date || defaults.resignation_date || ''
            }));
          }
        })
        .catch(err => console.error("Failed to fetch F&F defaults on mount", err));
    }
  }, [open, tableName, formData.assignment_id, record]);

  // --- ASSIGNMENT: Auto-fetch employee_code on load if COMPANY_id is set ---
  useEffect(() => {
    if (!open || tableName !== 'ASSIGNMENT' || record?.id) return;
    const companyId = formData.COMPANY_id || formData.company_id;
    if (companyId && !formData.employee_code) {
      fetchNextEmployeeCode(companyId)
        .then(res => {
          if (res.data?.success && res.data.employee_code) {
            setFormData(prev => ({ ...prev, employee_code: res.data.employee_code }));
          }
        })
        .catch(err => console.error("Failed to fetch initial employee code", err));
    }
  }, [open, tableName, formData.COMPANY_id, formData.company_id, formData.employee_code, record]);

  // PERSON: keep full_name + legislation in sync when names/company/options change
  useEffect(() => {
    if (!open || tableName !== 'PERSON' || !tableConfig) return;
    setFormData(prev => {
      const patch = {};

      const computeFull = tableConfig.fields?.full_name?.compute;
      if (typeof computeFull === 'function') {
        const full = computeFull(prev);
        if (full !== prev.full_name) patch.full_name = full;
      }

      if (prev.COMPANY_id) {
        const row = (ddOptions.COMPANY_id || []).find(o => String(o.value) === String(prev.COMPANY_id))?._row;
        const ldg = row?.default_ldg_id || '';
        if (ldg && ldg !== prev.ldg_id) patch.ldg_id = ldg;
      }

      if (!Object.keys(patch).length) return prev;
      return { ...prev, ...patch };
    });
  }, [
    open, tableName, tableConfig,
    formData.COMPANY_id, formData.first_name, formData.middle_name, formData.last_name,
    ddOptions.COMPANY_id,
  ]);

  // Evaluates `showWhen` / `hideWhen` rules against current formData.
  // Rule formats supported:
  //   { field: 'period_type', equals: 'monthly' }
  //   { field: 'period_type', in: ['monthly', 'biweekly'] }
  //   { field: 'interest_applicable', equals: 'Y' }
  const matchesRule = (rule) => {
    if (!rule || !rule.field) return true;
    const v = formData[rule.field];
    if (rule.equals !== undefined) return v === rule.equals;
    if (Array.isArray(rule.in)) return rule.in.includes(v);
    if (rule.notEquals !== undefined) return v !== rule.notEquals;
    if (rule.notEmpty) return v !== undefined && v !== null && v !== '';
    return true;
  };
  const isFieldVisible = (field) => {
    if (field === '_id' || field === tableConfig?.pk) return false;
    const cfg = tableConfig?.fields?.[field];
    if (tableConfig && (!cfg || cfg.type === 'auto' || cfg.type === 'auto-code')) return false;
    if (cfg?.showWhen && !matchesRule(cfg.showWhen)) return false;
    if (cfg?.hideWhen && matchesRule(cfg.hideWhen)) return false;
    return true;
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = useCallback(() => {
    const errs = {};
    if (!tableConfig) {
      schema.slice(0, 3).forEach(f => {
        if (f !== '_id' && !String(formData[f] ?? '').trim()) errs[f] = 'Required';
      });
      schema.forEach(f => {
        if (isLegacyNum(f) && formData[f] !== '' && isNaN(Number(formData[f]))) errs[f] = 'Must be a number';
        if (isLegacyEmail(f) && formData[f] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData[f])) errs[f] = 'Invalid email';
        if (f === 'start_time' || f === 'end_time') {
          const val = String(formData[f] ?? '').trim();
          if (!val) errs[f] = 'This field is required';
          if (f === 'end_time' && formData.start_time && val === formData.start_time) {
            errs[f] = 'End time cannot be the same as start time';
          }
        }
      });
      setErrors(errs);
      return !Object.keys(errs).length;
    }

    // Union of fields from backend schema AND fieldConfig — backend schema may not
    // include every form field for newer tables (legacy tableSchemas mockData lag);
    // fieldConfig is the authoritative source for the form's required fields.
    const fieldUnion = new Set([
      ...schema,
      ...Object.keys(tableConfig.fields || {}),
    ]);
    fieldUnion.forEach(field => {
      if (field === '_id' || field === tableConfig.pk) return;
      const cfg = tableConfig.fields[field];
      if (cfg && (cfg.type === 'auto' || cfg.type === 'auto-code' || cfg.type === 'image')) return;

      // Skip validation for fields hidden by conditional visibility rules
      if (!isFieldVisible(field)) return;

      if (cfg?.type === 'time' || field === 'start_time' || field === 'end_time') {
        const val = String(formData[field] ?? '').trim();
        const isReq = cfg?.dynamicRequired ? cfg.dynamicRequired(formData) : (cfg?.required ?? false);
        if (isReq && !val) { errs[field] = 'This field is required'; return; }
        if (!val) return;
        if (field === 'end_time' || cfg?.afterTimeField) {
          const startField = cfg?.afterTimeField || 'start_time';
          const startVal = String(formData[startField] ?? '').trim();
          if (startVal && val === startVal) {
            errs[field] = 'End time cannot be the same as start time';
          }
        }
        return;
      }

      if (!cfg) return;
      const isReq = cfg.dynamicRequired ? cfg.dynamicRequired(formData) : (cfg.required ?? false);

      // readOnly fields skip user-input validation EXCEPT for required check
      // (a readOnly required field must still have a value, just sourced auto-magically)
      if (cfg.readOnly && !isReq) return;
      const val = String(formData[field] ?? '').trim();
      if (cfg.readOnly) {
        if (isReq && !val) errs[field] = 'This field is required';
        return;
      }

      // Multi-dropdown: array-based validation
      if (cfg.type === 'multi-dropdown') {
        const arr = Array.isArray(formData[field]) ? formData[field] : [];
        const min = cfg.minItems ?? (isReq ? 1 : 0);
        const max = cfg.maxItems;
        if (isReq && arr.length === 0) { errs[field] = 'Select at least one'; return; }
        if (arr.length < min) { errs[field] = `Select at least ${min}`; return; }
        if (max !== undefined && arr.length > max) { errs[field] = `Max ${max} allowed`; return; }
        return;
      }

      // Geo-select and cascade-select types: just require/check
      if (['country-select', 'state-select', 'city-select', 'cascade-select'].includes(cfg.type)) {
        if (isReq && !val) errs[field] = 'This field is required';
        return;
      }
      // Postal code: pattern from selected country
      if (cfg.type === 'postal') {
        if (isReq && !val) { errs[field] = 'This field is required'; return; }
        if (val) {
          const cd = getCountryData(formData.country || '');
          if (cd?.postalPattern && !new RegExp(cd.postalPattern).test(val))
            errs[field] = cd.postalHint || 'Invalid postal code';
        }
        return;
      }

      // General required check for other fields
      if (isReq && !val) { errs[field] = 'This field is required'; return; }
      if (!val) return;

      // Defensive: even if dropdown already filters self out, catch manual API-style submits
      if (cfg.excludeIfMatches && String(val) === String(formData[cfg.excludeIfMatches] || '')) {
        const refLabel = tableConfig?.fields?.[cfg.excludeIfMatches]?.label || cfg.excludeIfMatches;
        errs[field] = `Cannot be the same as ${refLabel}`;
        return;
      }

      // `maxRefField`: this field's numeric value must be <= another field's numeric value
      // (e.g. approved_amount <= loan_amount)
      if (cfg.maxRefField) {
        const refVal = Number(formData[cfg.maxRefField]) || 0;
        const thisN = Number(val) || 0;
        if (refVal > 0 && thisN > refVal) {
          const refLabel = tableConfig?.fields?.[cfg.maxRefField]?.label || cfg.maxRefField;
          errs[field] = cfg.maxRefFieldMsg || `Cannot exceed ${refLabel} (${refVal.toLocaleString('en-IN')})`;
          return;
        }
      }

      // `minRefField`: this field's numeric value must be >= another field's numeric value
      // (e.g. max_salary >= min_salary)
      if (cfg.minRefField) {
        const refRaw = formData[cfg.minRefField];
        if (refRaw !== undefined && refRaw !== '') {
          const refVal = Number(refRaw);
          const thisN = Number(val);
          if (!isNaN(refVal) && !isNaN(thisN) && thisN < refVal) {
            const refLabel = tableConfig?.fields?.[cfg.minRefField]?.label || cfg.minRefField;
            errs[field] = `Cannot be less than ${refLabel} (${refVal.toLocaleString('en-IN')})`;
            return;
          }
        }
      }

      if (cfg.type === 'number') {
        if (isNaN(Number(val))) { errs[field] = 'Must be a number'; return; }
        const n = Number(val);
        if (cfg.min !== undefined && n < cfg.min) { errs[field] = `Min value is ${cfg.min}`; return; }
        if (cfg.max !== undefined && n > cfg.max) { errs[field] = `Max value is ${cfg.max}`; return; }
        if (cfg.integerOnly && !Number.isInteger(n)) { errs[field] = 'Must be a whole number'; return; }
      }
      if (cfg.type === 'text' || cfg.type === 'email') {
        if (cfg.maxLength && val.length > cfg.maxLength) { errs[field] = `Max ${cfg.maxLength} characters`; return; }
        if (cfg.minLength && val.length < cfg.minLength) { errs[field] = `Must be exactly ${cfg.minLength} characters`; return; }
        if (cfg.pattern && !new RegExp(cfg.pattern).test(val)) { errs[field] = cfg.patternMsg || 'Invalid format'; return; }
      }
      if (cfg.type === 'url') {
        const toTest = val.startsWith('http://') || val.startsWith('https://') ? val : `https://${val}`;
        try { new URL(toTest); } catch { errs[field] = 'Enter a valid URL (e.g. https://example.com)'; }
        return;
      }
      if (cfg.type === 'date') {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) { errs[field] = 'Format must be YYYY-MM-DD'; return; }
        if (isNaN(new Date(val).getTime())) { errs[field] = 'Invalid date'; return; }
        if (cfg.noFuture) {
          const today = new Date().toISOString().slice(0, 10);
          if (val > today) { errs[field] = 'Date cannot be in the future'; return; }
        }
        if (cfg.afterField) {
          const other = formData[cfg.afterField];
          if (other && new Date(val) <= new Date(other))
            errs[field] = cfg.afterFieldMsg || `Must be after ${tableConfig.fields[cfg.afterField]?.label || cfg.afterField}`;
        }
        if (cfg.betweenFields) {
          const [f, t] = cfg.betweenFields;
          const fv = formData[f], tv = formData[t];
          if (fv && tv && (new Date(val) < new Date(fv) || new Date(val) > new Date(tv)))
            errs[field] = `Must be between ${tableConfig.fields[f]?.label} and ${tableConfig.fields[t]?.label}`;
        }
      }
    });

    if (typeof tableConfig?.validate === 'function') {
      const customErrs = tableConfig.validate(formData, existingRecords);
      if (customErrs) {
        if (typeof customErrs === 'string') errs[tableConfig.pk || schema[0]] = customErrs;
        else Object.assign(errs, customErrs);
      }
    }

    // Filter errs to only include fields that are visible or required but not hidden by conditional logic
    // Actually, validate already skips isFieldVisible, but just to be sure we don't block on hidden custom errors
    const visibleErrs = {};
    Object.entries(errs).forEach(([k, v]) => {
      if (isFieldVisible(k)) visibleErrs[k] = v;
    });

    setErrors(visibleErrs);
    const isValid = !Object.keys(visibleErrs).length;
    if (!isValid) {
      setTimeout(() => {
        const firstErrorEl = document.querySelector('.Mui-error, [aria-invalid="true"]');
        if (firstErrorEl) {
          firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
    return isValid;
  }, [formData, schema, tableConfig, existingRecords]);

  // ── Change handler — supports cascading and auto-fill ────────────────────
  const handleChange = useCallback((field, value) => {
    if (isFormLocked) return;
    const cfg = tableConfig?.fields[field];
    const processed = cfg?.autoUppercase && typeof value === 'string' ? value.toUpperCase() : value;

    const updates = { [field]: processed };

    // Auto-fill currency when company_id / COMPANY_id / ldg_id changes
    if (field === 'ldg_id' && value && ddOptions['ldg_id']) {
      const ldgRow = ddOptions['ldg_id'].find(opt => opt.value === value)?._row;
      if (ldgRow?.currency_code) {
        const cField = 'currency' in tableConfig.fields ? 'currency' : 'currency_code';
        updates[cField] = ldgRow.currency_code;
      }
    } else if ((field === 'COMPANY_id' || field === 'company_id') && value && (ddOptions['COMPANY_id'] || ddOptions['company_id']) && ddOptions['ldg_id']) {
      const companyOptions = ddOptions['COMPANY_id'] || ddOptions['company_id'] || [];
      const companyRow = companyOptions.find(opt => opt.value === value)?._row;
      if (companyRow?.default_ldg_id) {
        const ldgRow = ddOptions['ldg_id'].find(opt => opt.value === companyRow.default_ldg_id)?._row;
        if (ldgRow?.currency_code) {
          const cField = 'currency' in tableConfig.fields ? 'currency' : 'currency_code';
          updates[cField] = ldgRow.currency_code;
        }
      }
    }

    if (tableName === 'ASSIGNMENT' && (field === 'COMPANY_id' || field === 'company_id') && value) {
      fetchNextEmployeeCode(value)
        .then(res => {
          if (res.data?.success && res.data.employee_code) {
            setFormData(prev => ({ ...prev, employee_code: res.data.employee_code }));
          }
        })
        .catch(err => console.error("Failed to fetch next employee code", err));
    }

    if (tableName === 'ATTENDANCE' && field === 'assignment_id' && value) {
      const assignmentOpts = ddOptions['assignment_id'] || [];
      const assignmentRow = assignmentOpts.find(opt => opt.value === value)?._row;
      if (assignmentRow?.person_id) {
        updates.person_id = assignmentRow.person_id;
      }
    }

    // --- ADVANCE SALARY: Auto-fill Recovery & EMI Schedule ---
    if (tableName === 'ADVANCE_SALARY' && ['assignment_id', 'number_of_installments', 'tenure_months', 'first_period_id', 'disbursement_date', 'payment_date', 'approval_date'].includes(field)) {
      const nextData = { ...formData, ...updates };

      const numInstallments = parseInt(nextData.number_of_installments, 10);
      const tenure = parseInt(nextData.tenure_months, 10);
      const firstPeriodId = nextData.first_period_id;

      // Get employee's payroll and sorted pay periods
      let payrollId = null;
      let sortedPeriods = [];
      if (nextData.assignment_id) {
        const assignmentOpts = ddOptions['assignment_id'] || [];
        const assignmentRow = assignmentOpts.find(opt => opt.value === nextData.assignment_id)?._row;
        if (assignmentRow && assignmentRow.payroll_id) {
          payrollId = assignmentRow.payroll_id;
          const employeePeriods = ddOptions['first_period_id']?.filter(opt => opt._row && String(opt._row.payroll_id) === String(payrollId)) || [];
          sortedPeriods = [...employeePeriods].sort((a, b) => (a._row.start_date || '').localeCompare(b._row.start_date || ''));
        }
      }

      // 1. Auto-select first recovery period if not set
      let resolvedFirstPeriodId = firstPeriodId;
      if (!resolvedFirstPeriodId && sortedPeriods.length > 0) {
        const baseDate = nextData.disbursement_date || nextData.payment_date || nextData.approval_date || new Date().toISOString().split('T')[0];
        const firstPeriodOpt = sortedPeriods.find(opt => (opt._row.end_date || '') >= baseDate) || sortedPeriods[0];
        if (firstPeriodOpt) {
          resolvedFirstPeriodId = firstPeriodOpt.value;
          updates.first_period_id = resolvedFirstPeriodId;
        }
      }

      // 2. If we have a first period, calculate the recovery start date, completion date, and last deduction date
      if (resolvedFirstPeriodId && sortedPeriods.length > 0) {
        const firstPeriodOpt = sortedPeriods.find(opt => opt.value === resolvedFirstPeriodId);
        const selectedPeriodRow = firstPeriodOpt?._row;

        if (selectedPeriodRow) {
          updates.recovery_start_date = selectedPeriodRow.start_date || '';

          const N = numInstallments || 1;

          let completionDate = '';
          let lastDeductionDate = '';

          const firstIdx = sortedPeriods.findIndex(opt => opt.value === resolvedFirstPeriodId);
          if (firstIdx !== -1 && sortedPeriods[firstIdx + N - 1]) {
            const finalPeriodRow = sortedPeriods[firstIdx + N - 1]._row;
            completionDate = finalPeriodRow.end_date || '';
            lastDeductionDate = finalPeriodRow.payment_date || finalPeriodRow.end_date || '';
          }

          // Helper to add months
          const addMonths = (dateStr, months) => {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return '';
            d.setMonth(d.getMonth() + months);
            return d.toISOString().split('T')[0];
          };

          // Fallback to month arithmetic if not found in database periods
          if (!completionDate && selectedPeriodRow.end_date) {
            completionDate = addMonths(selectedPeriodRow.end_date, N - 1);
          }
          if (!lastDeductionDate && (selectedPeriodRow.payment_date || selectedPeriodRow.end_date)) {
            lastDeductionDate = addMonths(selectedPeriodRow.payment_date || selectedPeriodRow.end_date, N - 1);
          }

          updates.recovery_completion_date = completionDate;
          updates.last_deduction_date = lastDeductionDate;

          const addDays = (dateStr, days) => {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return '';
            d.setDate(d.getDate() + days);
            return d.toISOString().split('T')[0];
          };

          // Suggest loan closure date based on last EMI deduction date + 1 day system buffer
          if (lastDeductionDate) {
            updates.loan_closure_date = addDays(lastDeductionDate, 1);
          }
        }
      }

      // 3. Auto-fill Disbursement Payment Section based on payroll configuration
      let resolvedDisbursementDate = nextData.disbursement_date;
      if (field === 'approval_date' && nextData.approval_date && !resolvedDisbursementDate) {
        resolvedDisbursementDate = nextData.approval_date;
        updates.disbursement_date = resolvedDisbursementDate;
      }

      // Once disbursed, the loan status becomes Active
      if (resolvedDisbursementDate && (!nextData.loan_status || ['PENDING', 'APPROVED', 'DISBURSED'].includes(nextData.loan_status))) {
        updates.loan_status = 'ACTIVE';
      }

      if (resolvedDisbursementDate && sortedPeriods.length > 0) {
        const currentPeriodOpt = sortedPeriods.find(opt => (opt._row.start_date || '') <= resolvedDisbursementDate && (opt._row.end_date || '') >= resolvedDisbursementDate)
          || sortedPeriods.find(opt => (opt._row.end_date || '') >= resolvedDisbursementDate)
          || sortedPeriods[0];

        if (currentPeriodOpt?._row) {
          updates.payment_date = currentPeriodOpt._row.payment_date || currentPeriodOpt._row.end_date || '';
        }
      }
    }
    // --- ADVANCE SALARY & REQUEST: Rejection details auto-populate ---
    if (tableName === 'ADVANCE_SALARY' && field === 'loan_status' && value === 'REJECTED') {
      updates.rejected_date = new Date().toISOString().split('T')[0];
    }
    if (tableName === 'ADVANCE_SALARY_REQUEST' && field === 'approval_status' && value === 'REJECTED') {
      updates.rejected_date = new Date().toISOString().split('T')[0];
    }

    // --- FORM 16 SUMMARY DEFAULTS FETCH ---
    if (tableName === 'FORM16_SUMMARY' && (field === 'assignment_id' || field === 'financial_year')) {
      const nextData = { ...formData, ...updates };
      const targetAsg = nextData.assignment_id;
      const targetFy = nextData.financial_year;

      if (targetAsg && targetFy && targetFy.length === 7) {
        fetchForm16Defaults(targetAsg, targetFy)
          .then(res => {
            if (res.data?.success && res.data?.data) {
              const defaults = res.data.data;
              setFormData(prev => ({
                ...prev,
                ...defaults,
                assignment_id: targetAsg,
                financial_year: targetFy
              }));
              setErrors(prev => {
                const next = { ...prev };
                delete next.financial_year;
                delete next.assignment_id;
                return next;
              });
            }
          })
          .catch(err => {
            console.error("Failed to fetch Form 16 defaults", err);
            const errMsg = err.response?.data?.message || err.userMessage || "Failed to fetch defaults";
            setErrors(prev => ({ ...prev, financial_year: errMsg }));
          });
      } else {
        setFormData(prev => ({
          ...prev,
          tan_number: '',
          pan_employee: '',
          gross_salary: '',
          exempt_allowances: '',
          standard_deduction: '',
          professional_tax: '',
          total_80c: '',
          total_80d: '',
          other_deductions: '',
          taxable_income: '',
          tax_on_income: '',
          surcharge: '',
          education_cess: '',
          total_tax_payable: '',
          tds_deducted: '',
          tax_regime: '',
          effective_from: '',
          effective_to: '',
          COMPANY_id: '',
          bg_id: '',
          ldg_id: ''
        }));
      }
    }

    // --- FULL & FINAL SETTLEMENT DEFAULTS FETCH ---
    if (tableName === 'FULL_FINAL_SETTLEMENT' && field === 'assignment_id') {
      if (processed) {
        fetchFfDefaults(processed)
          .then(res => {
            if (res.data?.success && res.data?.data) {
              const defaults = res.data.data;
              setFormData(prev => {
                const nextState = {
                  ...prev,
                  assignment_id: processed,
                  resignation_date: defaults.resignation_date || prev.resignation_date || '',
                  exit_date: defaults.exit_date || prev.exit_date || '',
                  notice_period_days: defaults.notice_period_days ?? prev.notice_period_days ?? 0,
                  actual_notice_served: defaults.actual_notice_served ?? prev.actual_notice_served ?? 0,
                  worked_days: defaults.worked_days ?? prev.worked_days ?? 0,
                  lop_days: defaults.lop_days ?? prev.lop_days ?? 0,
                  last_salary_amount: defaults.last_salary_amount ?? prev.last_salary_amount ?? 0,
                  leave_balance: defaults.leave_balance ?? prev.leave_balance ?? 0,
                  leave_encashment_amount: defaults.leave_encashment_amount ?? prev.leave_encashment_amount ?? 0,
                  advance_recovery: defaults.advance_recovery ?? prev.advance_recovery ?? 0,
                  loan_recovery: defaults.loan_recovery ?? prev.loan_recovery ?? 0,
                  currency: defaults.currency || prev.currency || 'INR',
                  notice_start_date: defaults.notice_start_date || prev.notice_start_date || '',
                  years_of_service: defaults.years_of_service ?? prev.years_of_service ?? 0,
                  gratuity_payable: defaults.gratuity_payable ?? prev.gratuity_payable ?? 0,
                  tds_on_settlement: defaults.tds_on_settlement ?? prev.tds_on_settlement ?? 0,
                  gross_settlement: defaults.gross_settlement ?? prev.gross_settlement ?? 0,
                  total_earnings: defaults.total_earnings ?? prev.total_earnings ?? 0,
                  total_deductions: defaults.total_deductions ?? prev.total_deductions ?? 0,
                  total_recovery: defaults.total_recovery ?? prev.total_recovery ?? 0,
                  net_payable: defaults.net_payable ?? prev.net_payable ?? 0,
                  recovery_lines: defaults.recovery_lines ?? prev.recovery_lines ?? [],
                  clearance_status: defaults.clearance_status || prev.clearance_status || 'Pending',
                  lifecycle_status: defaults.lifecycle_status || prev.lifecycle_status || 'Initiated'
                };
                if (tableConfig?.fields) {
                  for (let pass = 0; pass < 3; pass++) {
                    Object.entries(tableConfig.fields).forEach(([f, c]) => {
                      if (c?.recomputeOnChange && typeof c.compute === 'function') {
                        try {
                          const newVal = c.compute(nextState);
                          if (newVal !== undefined && newVal !== null) nextState[f] = newVal;
                        } catch (err) { }
                      }
                    });
                  }
                }
                return nextState;
              });
            }
          })
          .catch(err => {
            console.error("Failed to fetch F&F defaults", err);
          });
      } else {
        setFormData(prev => ({
          ...prev,
          exit_date: '',
          notice_period_days: '',
          actual_notice_served: '',
          worked_days: '',
          lop_days: '',
          last_salary_amount: '',
          leave_balance: '',
          leave_encashment_amount: '',
          advance_recovery: '',
          loan_recovery: '',
          currency: '',
          earned_salary: '',
          notice_pay_recovery: '',
          gross_settlement: '',
          total_deductions: '',
          net_payable: '',
          notice_start_date: '',
          years_of_service: '',
          gratuity_payable: '',
          tds_on_settlement: '',
          recovery_lines: [],
          clearance_status: 'Pending',
          lifecycle_status: 'Initiated'
        }));
      }
    }


    // Clear dependent (cascading) fields
    if (cfg?.cascade?.length) {
      cfg.cascade.forEach(dep => { updates[dep] = ''; });
    }
    // Auto-clear any sibling whose `excludeIfMatches` points at THIS field and currently
    // holds the same value (e.g. manager_id == newly-selected person_id — clear manager).
    if (processed && tableConfig?.fields) {
      Object.entries(tableConfig.fields).forEach(([f, c]) => {
        if (c?.excludeIfMatches === field && String(formData[f] || '') === String(processed)) {
          updates[f] = '';
        }
      });
    }
    // Auto-fill sibling fields from geo-country data
    if (cfg?.autoFill) {
      // Two data sources: COUNTRIES (currency code, countryCode, timeZone, postalPattern)
      // and CURRENCY_INFO via getCurrencyByCountry (name, symbol, decimals).
      // The dataKey is tried against country first, then currency — so a field config
      // can mix both: { currency_code: 'currency', symbol: 'symbol', decimal_places: 'decimals' }
      const countryData = getCountryData(processed);
      const currencyData = getCurrencyByCountry(processed);
      Object.entries(cfg.autoFill).forEach(([targetField, dataKey]) => {
        let value = countryData?.[dataKey];
        if (value === undefined || value === '' || value === null) {
          value = currencyData?.[dataKey];
        }
        updates[targetField] = value ?? '';
      });
    }

    // Auto-fill `effective_to` when `effective_from` is picked and `to` is empty
    if (field === 'effective_from' && processed && !formData.effective_to) {
      const toCfg = tableConfig?.fields?.effective_to;
      if (toCfg && toCfg.type === 'date') {
        updates.effective_to = '2099-12-31';
      }
    }

    // Generic per-field "defaultWhen" trigger — when this field is set,
    // auto-fill any sibling whose `defaultWhen.field` matches and is currently empty.
    if (processed && tableConfig?.fields) {
      Object.entries(tableConfig.fields).forEach(([f, c]) => {
        if (c?.defaultWhen?.field === field
          && !formData[f]
          && updates[f] === undefined) {
          updates[f] = c.defaultWhen.value;
        }
      });
    }

    // `mirrorField` — copy this field's value into sibling that mirrors it,
    // but only when the sibling is currently empty (don't override user input).
    if (processed && tableConfig?.fields) {
      Object.entries(tableConfig.fields).forEach(([f, c]) => {
        if (c?.mirrorField === field
          && !formData[f]
          && updates[f] === undefined) {
          updates[f] = processed;
        }
      });
    }

    // `autoFillFromFk` — when an FK dropdown changes, populate sibling fields
    // by reading columns directly off the selected source row. CHAINS recursively:
    // if a filled field is itself a dropdown with autoFillFromFk, run that too.
    const applyAutoFillFromFk = (srcField, srcValue) => {
      const sCfg = tableConfig?.fields?.[srcField];
      if (!sCfg?.autoFillFromFk) return;
      const opts = ddOptions[srcField] || [];
      const row = opts.find(o => String(o.value) === String(srcValue))?._row;
      if (row) {
        Object.entries(sCfg.autoFillFromFk).forEach(([target, readField]) => {
          const v = row[readField];
          if (v !== undefined && v !== null) {
            updates[target] = v;
            // Recurse if target is itself an FK with autoFillFromFk
            applyAutoFillFromFk(target, v);
          } else {
            updates[target] = '';
          }
        });
      } else if (!srcValue) {
        // FK cleared — also clear auto-filled siblings
        Object.keys(sCfg.autoFillFromFk).forEach(target => { updates[target] = ''; });
      }
    };
    if (cfg?.autoFillFromFk) {
      applyAutoFillFromFk(field, processed);
    }

    // `autoSelectFrom` — when this field changes, look up another dropdown's options
    // and auto-select the FIRST option whose row matches a column to this field's value.
    // Config: { otherFieldName: 'columnNameOnOtherDropdownRows' }
    // Example: on person_id → { assignment_id: 'person_id' } — picking a Person
    //          auto-selects the first Assignment whose person_id matches.
    if (cfg?.autoSelectFrom && cfg?.type === 'dropdown' && processed) {
      Object.entries(cfg.autoSelectFrom).forEach(([otherField, matchCol]) => {
        const otherOpts = ddOptions[otherField] || [];
        const matching = otherOpts.find(o => o._row && String(o._row[matchCol]) === String(processed));
        if (matching) {
          updates[otherField] = matching.value;
          applyAutoFillFromFk(otherField, matching.value);
        }
      });
    }

    // If a salary template dropdown was changed and it auto-filled amounts,
    // compute the increment % from prev_basic -> new_basic so the form shows it.
    // We derive using the synthetic next state so any newly-filled new_basic is used.
    // Keep fields editable — we only set the computed percentage value.
    const _nextForInc = { ...formData, ...updates };
    if (_nextForInc.prev_basic !== undefined && _nextForInc.new_basic !== undefined) {
      const pb = Number(_nextForInc.prev_basic) || 0;
      const nb = Number(_nextForInc.new_basic) || 0;
      if (pb > 0) {
        // two-decimal precision
        updates.increment_pct = Number((((nb - pb) / pb) * 100).toFixed(2));
      } else {
        updates.increment_pct = '';
      }
    }
    if (_nextForInc.new_basic !== undefined) {
      const nb = Number(_nextForInc.new_basic) || 0;
      updates.new_gross = nb > 0 ? Number((nb / 12).toFixed(2)) : '';
    }

    // `recomputeOnChange` + `compute` — any field with these recalculates on every
    // state change. Uses the synthetic next state ({ ...formData, ...updates }) so
    // multiple compute fields can depend on values just being changed.
    if (tableConfig?.fields) {
      const nextState = { ...formData, ...updates };
      Object.entries(tableConfig.fields).forEach(([f, c]) => {
        if (c?.recomputeOnChange && typeof c.compute === 'function') {
          try {
            const newVal = c.compute(nextState);
            if (newVal !== undefined && newVal !== null) updates[f] = newVal;
          } catch { /* swallow compute errors — leave stale value */ }
        }
      });
    }

    // --- ABSENCE TYPE Custom Auto-code ---
    if (tableName === 'ABSENCE_TYPE' && field === 'absence_name' && typeof processed === 'string') {
      updates.absence_code = processed.toUpperCase().replace(/[^A-Z]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').slice(0, 20);
    }

    // --- PERSONAL PAYMENT METHOD: Auto-fetch prel_id ---
    if (tableName === 'PERSONAL_PAYMENT_METHOD' && field === 'assignment_id') {
      const asnOpts = ddOptions['assignment_id'] || [];
      const asnOpt = asnOpts.find(o => String(o.value) === String(processed));
      if (asnOpt?._row?.person_id) {
        const prelOpts = ddOptions['prel_id'] || [];
        const matchingPrel = prelOpts.find(o => String(o._row?.person_id) === String(asnOpt._row.person_id));
        if (matchingPrel) {
          updates.prel_id = matchingPrel.value;
        } else {
          updates.prel_id = '';
        }
      } else if (!processed) {
        updates.prel_id = '';
      }
    }

    // --- PF ACCOUNT Custom Assignment Handling ---
    if (tableName === 'PF_ACCOUNT' && field === 'assignment_id') {
      const asnOpts = ddOptions['assignment_id'] || [];
      const asnOpt = asnOpts.find(o => o.value === processed);
      const row = asnOpt?._row;

      if (!row) {
        updates.monthly_pf_ee = '';
        updates.monthly_pf_er = '';
        updates.monthly_eps_er = '';
        updates.monthly_edli_er = '';
        updates.pf_estab_code = '';
        updates.pf_office_code = '';
        updates.ytd_pf_ee = '';
        updates.ytd_pf_er = '';
        updates.ytd_eps_er = '';
        setErrors(prev => { const n = { ...prev }; delete n.assignment_id; return n; });
      } else {
        let errorMsg = null;

        // Auto-fill from company statutory configs if they exist
        if (row.company?.statutory_configurations) {
          const pfConfig = row.company.statutory_configurations.find(c => c.component_code === 'PF' || c.component_code === 'EPF');
          if (pfConfig?.configuration_json) {
            const config = pfConfig.configuration_json;
            updates.pf_estab_code = config.estab_code || config.epf_establishment_code || updates.pf_estab_code || '';

            let parsedOffice = '';
            if (config.epf_establishment_code && typeof config.epf_establishment_code === 'string') {
              const parts = config.epf_establishment_code.split('/');
              if (parts.length >= 2) {
                parsedOffice = parts[1];
              }
            }
            updates.pf_office_code = config.office_code || parsedOffice || updates.pf_office_code || '';
          }
        }

        // Process Salary Template
        if (!row.default_template) {
          errorMsg = 'No Salary Template is assigned to this employee.';
          updates.monthly_pf_ee = '';
          updates.monthly_pf_er = '';
          updates.monthly_eps_er = '';
          updates.monthly_edli_er = '';
          updates.ytd_pf_ee = '';
          updates.ytd_pf_er = '';
          updates.ytd_eps_er = '';
        } else {
          const els = row.default_template.elements || [];
          const pfEl = els.find(e => e.element?.element_name?.toLowerCase().includes('provident') || e.element?.element_name?.toLowerCase() === 'pf');
          const basicEl = els.find(e => e.element?.element_name?.toLowerCase().includes('basic'));

          if (!pfEl) {
            errorMsg = 'Provident Fund component is not configured in the assigned Salary Template.';
            updates.monthly_pf_ee = '';
            updates.monthly_pf_er = '';
            updates.monthly_eps_er = '';
            updates.monthly_edli_er = '';
          } else {
            const pfAmt = pfEl.monthly_amount || 0;
            const basicAmt = basicEl?.monthly_amount || pfAmt * (100 / 12); // rough fallback

            updates.basic_monthly = basicAmt;
            updates.monthly_pf_ee = pfAmt;
            updates.monthly_pf_er = pfAmt;

            const cappedBasic = Math.min(basicAmt, 15000);
            updates.monthly_eps_er = Math.round(cappedBasic * 0.0833);
            updates.monthly_edli_er = Math.round(cappedBasic * 0.005);
          }

          updates.ytd_pf_ee = row.ytd_pf_ee || 0;
          updates.ytd_pf_er = row.ytd_pf_er || 0;
          updates.ytd_eps_er = row.ytd_eps_er || 0;
        }

        if (errorMsg) {
          setErrors(prev => ({ ...prev, assignment_id: errorMsg }));
        } else {
          setErrors(prev => { const n = { ...prev }; delete n.assignment_id; return n; });
        }
      }
    }

    // --- ESI ACCOUNT Custom Assignment Handling ---
    if (tableName === 'ESI_ACCOUNT' && field === 'assignment_id') {
      const asnOpts = ddOptions['assignment_id'] || [];
      const asnOpt = asnOpts.find(o => o.value === processed);
      const row = asnOpt?._row;

      if (!row) {
        updates.gross_ceiling = '';
        updates.monthly_esi_ee = '';
        updates.monthly_esi_er = '';
        updates.ytd_esi_ee = '';
        updates.ytd_esi_er = '';
        updates.esi_estab_code = '';
        setErrors(prev => { const n = { ...prev }; delete n.assignment_id; return n; });
      } else {
        let errorMsg = null;

        // Auto-fill from company statutory configs if they exist
        let eeRate = 0.75;
        let erRate = 3.25;
        if (row.company?.statutory_configurations) {
          const esiConfig = row.company.statutory_configurations.find(c => c.component_code === 'ESI');
          if (esiConfig?.configuration_json) {
            const config = esiConfig.configuration_json;
            updates.gross_ceiling = config.esi_wage_ceiling !== undefined ? config.esi_wage_ceiling : 21000;
            updates.esi_estab_code = config.esi_employer_code || updates.esi_estab_code || '';
            if (config.employee_esi_rate) eeRate = config.employee_esi_rate;
            if (config.employer_esi_rate) erRate = config.employer_esi_rate;
          }
        }

        // Process Salary Template
        if (!row.default_template) {
          errorMsg = 'No Salary Template is assigned to this employee.';
          updates.monthly_esi_ee = '';
          updates.monthly_esi_er = '';
          updates.ytd_esi_ee = '';
          updates.ytd_esi_er = '';
        } else {
          const els = row.default_template.elements || [];
          const esiEl = els.find(e => e.element?.element_name?.toLowerCase().includes('insuranceperson') || e.element?.element_name?.toLowerCase().includes('state insurance') || e.element?.element_name?.toLowerCase() === 'esi' || e.element?.element_code === 'ESI' || e.element?.element_code === 'ESI_EE');

          if (!esiEl) {
            updates.monthly_esi_ee = 0;
            updates.monthly_esi_er = 0;
          } else {
            const esiAmt = esiEl.monthly_amount || 0;
            updates.monthly_esi_ee = esiAmt;
            updates.monthly_esi_er = Math.round(esiAmt * (erRate / eeRate));
          }

          updates.ytd_esi_ee = row.ytd_esi_ee || 0;
          updates.ytd_esi_er = row.ytd_esi_er || 0;
        }

        if (errorMsg) {
          setErrors(prev => ({ ...prev, assignment_id: errorMsg }));
        } else {
          setErrors(prev => { const n = { ...prev }; delete n.assignment_id; return n; });
        }
      }
    }

    // `fyAutoCalcTo` — when an FY Start Month is selected, auto-fill the target
    // End Month with the previous month (so April start → March end = 12-month cycle).
    if (processed && cfg?.fyAutoCalcTo) {
      const idx = MONTHS.indexOf(processed);
      if (idx >= 0) {
        const endIdx = (idx + 11) % 12;
        updates[cfg.fyAutoCalcTo] = MONTHS[endIdx];
      }
    }

    // `autoGenFrom` — live-generate a CODE from a NAME field
    // e.g. module_code auto-derived from module_name: "Workforce Mgmt" → "WORKFORCE_MGMT"
    // Field config: { autoGenFrom: 'module_name', autoGenStyle: 'upper_underscore' | 'first_letters' }
    if (processed && tableConfig?.fields) {
      Object.entries(tableConfig.fields).forEach(([targetField, c]) => {
        if (c?.autoGenFrom === field && updates[targetField] === undefined) {
          // Skip if user has manually edited the target field (would have value but autoGenFrom flag set)
          // We use the convention: target is auto-generated until user types into it directly.
          // Existing record being edited → don't auto-overwrite.
          if (record && formData[targetField]) return;

          const src = String(processed).trim();
          if (!src) { updates[targetField] = ''; return; }

          const style = c.autoGenStyle || 'upper_underscore';
          let code;
          if (style === 'first_letters') {
            // "Indian Rupee" → "IR", "US Dollar" → "USD"
            code = src.split(/\s+/).map(w => w.charAt(0).toUpperCase()).join('').slice(0, c.maxLength || 10);
          } else if (style === 'compact') {
            // "Workforce Mgmt" → "WORKFORCEMGMT"
            code = src.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, c.maxLength || 20);
          } else if (style === 'short_prefix_01') {
            // "Advance Salary" → "ADV_SAL_01"
            const parts = src.split(/\s+/).map(w => w.replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase());
            code = parts.join('_') + '_01';
            code = code.slice(0, c.maxLength || 20);
          } else {
            // Default: upper_underscore — "Workforce Mgmt" → "WORKFORCE_MGMT"
            code = src.replace(/[^A-Za-z0-9\s]/g, '').trim().replace(/\s+/g, '_').toUpperCase().slice(0, c.maxLength || 20);
          }
          updates[targetField] = code;
        }
      });
    }

    setFormData(prev => ({ ...prev, ...updates }));
    // Clear errors for every touched field
    const changed = Object.keys(updates);
    if (changed.some(f => errors[f])) {
      setErrors(prev => {
        const next = { ...prev };
        changed.forEach(f => delete next[f]);
        return next;
      });
    }
  }, [tableConfig, errors, ddOptions, formData, record, isFormLocked]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    const data = { ...formData };
    if (tableConfig) {
      // Generate PK first — auto-code depends on it
      const compId =
        activeContext?.company_id ||
        data?.COMPANY_id ||
        data?.company_id ||
        '';

      const pkValue = !record
        ? generatePK(tableConfig, existingRecords, compId)
        : (data[tableConfig.pk] || '');

      // const compId = state?.activeContext?.company_id || data?.COMPANY_id || data?.company_id || '';
      // const pkValue = !record ? generatePK(tableConfig, existingRecords, compId) : (data[tableConfig.pk] || '');
      if (!record) data[tableConfig.pk] = pkValue;

      Object.entries(tableConfig.fields).forEach(([f, cfg]) => {
        if (cfg.type === 'auto') {
          data[f] = cfg.autoValue || 'SYSTEM';
        }
        // Derive code from PK numeric suffix — server will override anyway, but we send a valid preview
        if (cfg.type === 'auto-code' && pkValue) {
          let numPart = pkValue.replace(tableConfig.pkPrefix, '');
          // Re-pad to codePad digits if specified (independent of pkPad)
          if (cfg.codePad) numPart = String(parseInt(numPart, 10) || 0).padStart(cfg.codePad, '0');
          // Optionally insert the current year between prefix and sequence (e.g. GLAM-CG-2026-0001)
          const yearPart = cfg.codeIncludeYear ? (new Date().getFullYear() + '-') : '';

          let prefix = cfg.codePrefix || '';
          if (typeof cfg.dynamicPrefix === 'function') {
            prefix = cfg.dynamicPrefix(formData) || prefix;
          }

          data[f] = prefix + yearPart + numPart;
        }
        if (cfg.type === 'computed' && typeof cfg.compute === 'function') {
          try {
            data[f] = cfg.compute(formData, existingRecords, ddOptions) || '';
          } catch (err) {
            data[f] = '';
          }
        }
      });
    }
    delete data._id;
    setIsSubmitting(true);
    try {
      await onSave(data);
      setShowSuccess(true);
    } catch (err) {
      // Map backend error to per-field error highlight so the user sees
      // exactly which field is wrong (matches Phase 4C from validation Excel).
      const status = err?.status || err?.response?.status;
      const meta = err?.meta || err?.response?.data?.meta;
      const issues = err?.response?.data?.issues || err?.meta?.issues || [];
      const msg = err?.userMessage || err?.response?.data?.message || err?.message || 'Save failed';

      const fieldErrors = {};
      if (status === 409 && meta?.target) {
        const targets = Array.isArray(meta.target) ? meta.target : [meta.target];
        targets.forEach((t) => {
          fieldErrors[t] = `${tableConfig?.fields?.[t]?.label || t}: already exists with this value`;
        });
      }
      if ((status === 400 || status === 409) && Array.isArray(issues) && issues.length) {
        issues.forEach((issue) => {
          if (typeof issue !== 'string') return;
          const [path, ...rest] = issue.split(':');
          const field = path?.split('.')?.[0];
          const message = rest.join(':').trim() || issue;
          if (field) {
            fieldErrors[field] = message;
          }
        });
      }
      if (status === 413) {
        const imgField = Object.entries(tableConfig?.fields || {}).find(([_, c]) => c?.type === 'image');
        if (imgField) {
          fieldErrors[imgField[0]] = msg;
        }
      }
      if (Object.keys(fieldErrors).length) {
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
        setTimeout(() => {
          const firstErrorEl = document.querySelector('.Mui-error, [aria-invalid="true"]');
          if (firstErrorEl) {
            firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
      // Error is already shown in TablePage's snackbar, and field errors are set above.
      // Do NOT re-throw here, as it causes an Uncaught Promise Rejection in the React event loop.
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Conditional visibility (Phase 7D) ─────────────────────────────────────

  // ── Visible fields ────────────────────────────────────────────────────────
  // 'auto-code' IS visible (read-only preview); only plain 'auto' is hidden
  const allFields = Array.from(new Set([...schema, ...(tableConfig ? Object.keys(tableConfig.fields || {}) : [])]));
  const visibleFields = allFields.filter(field => {
    if (field === '_id') return false;
    if (tableConfig) {
      if (field === tableConfig.pk) return false;
      if (tableConfig.fields[field]?.type === 'auto') return false;
      // Phase 7D: conditional visibility
      if (!isFieldVisible(field)) return false;
    }
    return true;
  });

  // Ensure cascade-parent fields render BEFORE their children (e.g. bg_id before COMPANY_id,
  // country before state, state before city). The schema prop comes from the backend in
  // Prisma column order, which may place children first. We post-sort so any field with
  // `dependsOn: X` is moved to immediately after X in the render order.
  if (tableConfig?.fields) {
    for (let i = visibleFields.length - 1; i >= 0; i--) {
      const cfg = tableConfig.fields[visibleFields[i]];
      const parent = cfg?.dependsOn;
      if (!parent) continue;
      const parentIdx = visibleFields.indexOf(parent);
      if (parentIdx > i) {
        // Child currently before parent — move child to just after parent
        const [child] = visibleFields.splice(i, 1);
        visibleFields.splice(parentIdx, 0, child); // parentIdx unchanged after removal-of-earlier-element via splice means insert right after parent
      }
    }
  }

  const dropdownFields = visibleFields.filter(f => {
    const t = tableConfig?.fields[f]?.type;
    return t === 'dropdown' || t === 'multi-dropdown';
  });
  const dateFields = visibleFields.filter(f =>
    tableConfig?.fields[f]?.type === 'date' || (!tableConfig?.fields[f] && isLegacyDate(f)) ||
    tableConfig?.fields[f]?.type === 'time' || f === 'start_time' || f === 'end_time'
  );
  const flagFields = visibleFields.filter(f => tableConfig?.fields[f]?.type === 'flag');
  const otherFields = visibleFields.filter(f =>
    !dropdownFields.includes(f) && !dateFields.includes(f) && !flagFields.includes(f));

  const errorCount = Object.keys(errors).filter(f => isFieldVisible(f)).length;

  // ── Field renderers ───────────────────────────────────────────────────────
  const renderField = (field) => {
    const cfg = tableConfig?.fields[field];
    const resolvedHint = typeof cfg?.hint === 'function' ? cfg.hint(formData) : (cfg?.hint || '');
    const label = cfg?.label || toLabel(field);
    const err = errors[field];
    const val = formData[field] ?? '';
    const req = cfg?.dynamicRequired ? cfg.dynamicRequired(formData) : (cfg?.required ?? false);

    // Helper to determine if field is auto-filled by another currently populated field
    const getAutoFilledBy = (targetField) => {
      for (const [srcField, srcCfg] of Object.entries(tableConfig?.fields || {})) {
        if (srcCfg?.autoFillFromFk && srcCfg.autoFillFromFk[targetField]) {
          if (formData[srcField]) return { sourceField: srcField, label: srcCfg.label || toLabel(srcField) };
        }
      }
      return null;
    };
    const autoFilledBy = getAutoFilledBy(field);
    const isAutoFilledLocked = autoFilledBy && cfg?.readOnlyIfAutoFilled;

    // ── Computed / derived (read-only) ──────────────────────────────────
    if (cfg?.type === 'computed') {
      let computed = '';
      try { computed = typeof cfg.compute === 'function' ? cfg.compute(formData, existingRecords, ddOptions) : ''; }
      catch { computed = '—'; }
      return (
        <Box sx={{
          px: 2, py: 1.5, borderRadius: '10px',
          border: '1.5px dashed #BBF7D0', bgcolor: '#F0FDF4',
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <AutoAwesomeIcon sx={{ fontSize: 18, color: '#16a34a' }} />
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={10.5} fontWeight={700} color="#15803d"
              sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}>
              {label}
            </Typography>
            <Typography fontSize={14} fontWeight={700} color="#111827">
              {computed || '—'}
            </Typography>
          </Box>
        </Box>
      );
    }

    if (cfg?.type === 'custom-recoveries') {
      return (
        <FFRecoveryPanel
          formData={formData}
          setFormData={setFormData}
          readOnly={isFormLocked || cfg.readOnly}
        />
      );
    }

    // ── Country searchable dropdown ───────────────────────────────────────
    if (cfg?.type === 'country-select') {
      return (
        <Autocomplete
          options={COUNTRIES.map(c => c.name)}
          value={val || null}
          onChange={(_, v) => handleChange(field, v || '')}
          disabled={isFormLocked}
          renderInput={params => (
            <TextField {...autoTF(params, { label, req, err })} />
          )}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: '8px' } }}
          ListboxProps={{ sx: { fontSize: 13 } }}
        />
      );
    }

    // ── State searchable dropdown (depends on country) ────────────────────
    if (cfg?.type === 'state-select') {
      const parentVal = cfg.dependsOn ? (formData[cfg.dependsOn] || '') : '';
      const options = parentVal ? getStates(parentVal) : [];
      return (
        <Autocomplete
          options={options}
          value={val || null}
          disabled={isFormLocked || !parentVal}
          onChange={(_, v) => handleChange(field, v || '')}
          renderInput={params => (
            <TextField {...autoTF(params, {
              label, req, err,
              hint: !parentVal ? 'Select a country first' : '\u00A0',
              hintStyle: !parentVal ? { fontStyle: 'italic', color: '#94A3B8' } : {},
            })} />
          )}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: '8px' } }}
          ListboxProps={{ sx: { fontSize: 13 } }}
        />
      );
    }

    // ── City searchable dropdown (depends on state) ───────────────────────
    if (cfg?.type === 'city-select') {
      const parentVal = cfg.dependsOn ? (formData[cfg.dependsOn] || '') : '';
      const options = parentVal ? getCities(parentVal) : [];
      return (
        <Autocomplete
          options={options}
          value={val || null}
          disabled={isFormLocked || !parentVal}
          onChange={(_, v) => handleChange(field, v || '')}
          renderInput={params => (
            <TextField {...autoTF(params, {
              label, req, err,
              hint: !parentVal ? 'Select a state first' : '\u00A0',
              hintStyle: !parentVal ? { fontStyle: 'italic', color: '#94A3B8' } : {},
            })} />
          )}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: '8px' } }}
          ListboxProps={{ sx: { fontSize: 13 } }}
        />
      );
    }

    // ── Postal code ───────────────────────────────────────────────────────
    if (cfg?.type === 'postal') {
      const cd = getCountryData(formData.country || '');
      const hint = cd?.postalHint || 'Enter postal code';
      return (
        <TextField fullWidth size="small" required={req} label={label} value={val}
          onChange={e => handleChange(field, e.target.value)}
          disabled={isFormLocked}
          error={!!err} helperText={err || hint}
          inputProps={{ maxLength: 12, readOnly: isFormLocked }}
          InputProps={{ sx: { fontSize: 13, borderRadius: '8px' } }}
          InputLabelProps={{ sx: { fontSize: 13 } }}
          FormHelperTextProps={{ sx: { fontSize: 11 } }}
        />
      );
    }

    // ── Auto-generated code (read-only preview) ───────────────────────────
    if (cfg?.type === 'auto-code') {
      // Compute what the server will assign (best-effort preview)
      const compId =
        activeContext?.company_id ||
        record?.COMPANY_id ||
        record?.company_id ||
        '';

      const nextPk = !record
        ? generatePK(tableConfig, existingRecords, compId)
        : null;
      // const compId = state?.activeContext?.company_id || record?.COMPANY_id || record?.company_id || '';
      // const nextPk = !record ? generatePK(tableConfig, existingRecords, compId) : null;
      let numPart = nextPk
        ? nextPk.replace(tableConfig?.pkPrefix || '', '')
        : (val ? val.replace(cfg.codePrefix || '', '').replace(/^\d{4}-/, '') : '');
      // Re-pad to codePad digits if specified (allows code-pad to differ from pk-pad)
      if (cfg.codePad) numPart = String(parseInt(numPart, 10) || 0).padStart(cfg.codePad, '0');
      // Optionally insert the current year between prefix and sequence (e.g. GLAM-CG-2026-0001)
      const yearPart = cfg.codeIncludeYear ? (new Date().getFullYear() + '-') : '';
      const preview = (cfg.codePrefix || '') + yearPart + numPart;

      if (!record) {
        // ── New record: animated preview badge ──────────────────────────
        return (
          <Box sx={{
            p: 1.75, borderRadius: '10px',
            border: '1.5px dashed #bbf7d0',
            bgcolor: '#f0fdf4',
          }}>
            {/* Label row */}
            <Stack direction="row" alignItems="center" gap={1} mb={1}>
              <AutoAwesomeIcon sx={{ fontSize: 14, color: '#15803d' }} />
              <Typography fontSize={12} fontWeight={700} color="#6D28D9" letterSpacing={0.3} textTransform="uppercase">
                {label}
              </Typography>
            </Stack>

            {/* Code preview + badge */}
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
              <Box sx={{
                px: 1.75, py: 0.6, borderRadius: '8px',
                bgcolor: '#dcfce7', border: '1px solid #bbf7d0',
                fontFamily: 'monospace',
              }}>
                <Typography fontSize={15} fontWeight={800} color="#14532d" letterSpacing={1}>
                  {preview}
                </Typography>
              </Box>
              <Chip
                label="Auto-Generated"
                size="small"
                icon={<AutoAwesomeIcon sx={{ fontSize: '11px !important', color: '#A16207 !important' }} />}
                sx={{
                  height: 22, fontSize: 10.5, fontWeight: 700,
                  bgcolor: '#FEF9C3', color: '#A16207',
                  border: '1px solid #FDE68A',
                  '& .MuiChip-icon': { ml: 0.5 },
                }}
              />
            </Stack>

            {/* Hint */}
            <Typography fontSize={10.5} color="#6D28D9" sx={{ opacity: 0.6, mt: 0.75, fontStyle: 'italic' }}>
              Assigned by the system on save — cannot be edited
            </Typography>
          </Box>
        );
      }

      // ── Existing record: locked read-only field ──────────────────────
      return (
        <TextField fullWidth size="small" label={label} value={val}
          InputProps={{
            readOnly: true,
            sx: {
              fontSize: 14, fontWeight: 700, borderRadius: '8px',
              bgcolor: '#dcfce7', fontFamily: 'monospace', letterSpacing: 0.8,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#bbf7d0' },
            },
            endAdornment: (
              <InputAdornment position="end">
                <LockOutlinedIcon sx={{ fontSize: 14, color: '#16a34a' }} />
              </InputAdornment>
            ),
          }}
          InputLabelProps={{ shrink: true, sx: { fontSize: 13 } }}
          helperText="System-generated — cannot be changed"
          FormHelperTextProps={{ sx: { fontSize: 11, color: '#16a34a', fontStyle: 'italic' } }}
        />
      );
    }

    // ── Cascade-select (searchable, parent-filtered Autocomplete) ─────────
    if (cfg?.type === 'cascade-select') {
      const parentVal = cfg.dependsOn ? (formData[cfg.dependsOn] || '') : null;
      const allRecords = cascadeRawData[cfg.source] || [];
      const filtered = parentVal && cfg.filterBy
        ? allRecords.filter(r => r[cfg.filterBy] === parentVal)
        : (cfg.dependsOn ? [] : allRecords);
      const options = filtered.map(r => ({ value: r[cfg.valueField], label: r[cfg.labelField] || r[cfg.valueField] }));
      const selected = options.find(o => o.value === val) || null;
      const disabled = isFormLocked || (!!cfg.dependsOn && !parentVal);
      const parentLabel = cfg.dependsOn
        ? (tableConfig?.fields[cfg.dependsOn]?.label || cfg.dependsOn)
        : '';
      return (
        <Autocomplete
          options={options}
          getOptionLabel={o => typeof o === 'object' ? o.label : o}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          value={selected}
          disabled={disabled}
          onChange={(_, v) => handleChange(field, v?.value || '')}
          noOptionsText={disabled ? `Select a ${parentLabel} first` : 'No options'}
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              <Box>
                <Typography fontSize={13} lineHeight={1.3}>{option.label}</Typography>
                {option.label !== option.value && (
                  <Typography variant="caption" color="text.secondary" fontSize={10}>{option.value}</Typography>
                )}
              </Box>
            </li>
          )}
          renderInput={params => (
            <TextField
              {...autoTF(params, {
                label, req, err,
                hint: disabled ? `Select a ${parentLabel} first` : '\u00A0',
                hintStyle: disabled ? { fontStyle: 'italic', color: '#94A3B8' } : {},
              })}
              placeholder={disabled ? '' : (cfg.placeholder || `Select ${label}`)}
            />
          )}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: '8px' } }}
          ListboxProps={{ sx: { fontSize: 13 } }}
        />
      );
    }

    // ── FK Multi-Dropdown (array of values) ───────────────────────────────
    if (cfg?.type === 'multi-dropdown') {
      const loading = ddLoading[field];
      const options = ddOptions[field] || [];
      const selected = Array.isArray(val) ? val : (val ? [val] : []);
      const labelMap = Object.fromEntries(options.map(o => [o.value, o.label]));
      return (
        <FormControl fullWidth size="small" error={!!err} required={req} disabled={isFormLocked || loading}>
          <InputLabel sx={{ fontSize: 13 }}>{label}</InputLabel>
          <Select
            multiple value={selected} label={label}
            disabled={isFormLocked || loading}
            onChange={e => handleChange(field, e.target.value)}
            sx={{ fontSize: 13, borderRadius: '8px', minHeight: 40 }}
            renderValue={(arr) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {arr.map(v => (
                  <Box key={v} sx={{
                    bgcolor: '#dcfce7', color: '#15803D',
                    border: '1px solid #bbf7d0', borderRadius: '5px',
                    px: 0.8, py: 0.1, fontSize: 11, fontWeight: 600,
                  }}>
                    {labelMap[v] || v}
                  </Box>
                ))}
              </Box>
            )}
            startAdornment={loading ? <CircularProgress size={13} sx={{ mr: 1 }} /> : null}
          >
            {loading && <MenuItem disabled><em>Loading…</em></MenuItem>}
            {!loading && options.length === 0 && <MenuItem disabled><em>No records found</em></MenuItem>}
            {!loading && options.map(opt => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 14, height: 14, borderRadius: '3px',
                    border: '1.5px solid',
                    borderColor: selected.includes(opt.value) ? '#22c55e' : '#CBD5E1',
                    bgcolor: selected.includes(opt.value) ? '#22c55e' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: 11, fontWeight: 700,
                  }}>
                    {selected.includes(opt.value) ? '✓' : ''}
                  </Box>
                  <Box>
                    <Typography variant="body2" fontSize={13} lineHeight={1.3}>{opt.label}</Typography>
                    {opt.label !== opt.value && (
                      <Typography variant="caption" color="text.secondary" fontSize={10}>{opt.value}</Typography>
                    )}
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
          <FormHelperText sx={{ fontSize: 11 }}>
            {err || (selected.length > 0 ? `${selected.length} selected` : ' ')}
          </FormHelperText>
        </FormControl>
      );
    }

    // ── FK Dropdown (supports cascading via dependsOn + filterBy) ─────────
    if (cfg?.type === 'dropdown') {
      const loading = ddLoading[field];
      const _allOptions = ddOptions[field] || [];
      const _parentField = cfg.dependsOn || null;
      const _parentVal = _parentField ? (formData[_parentField] || '') : '';
      let options = _parentField
        ? (_parentVal
          ? (cfg.filterBy
            ? _allOptions.filter(o => o._row && String(o._row[cfg.filterBy]) === String(_parentVal))
            : _allOptions)
          : [])
        : _allOptions;

      if (_parentVal && cfg.filterVia) {
        const viaRows = (ddOptions[cfg.filterVia.field] || [])
          .map(o => o._row)
          .filter(Boolean)
          .filter(row => String(row[cfg.filterVia.parentKey]) === String(_parentVal));
        const allowedValues = new Set(
          viaRows
            .map(row => row[cfg.filterVia.valueKey])
            .flatMap(v => Array.isArray(v) ? v : [v])
            .filter(v => v !== undefined && v !== null && v !== '')
            .map(String)
        );
        options = _allOptions.filter(o => allowedValues.has(String(o.value)));
      }
      // Tolerant fallback: keep an existing value visible even if it doesn't match the filter
      // or if options are still loading, to prevent MUI out-of-range warnings.
      if (val && !options.find(o => String(o.value) === String(val))) {
        const legacy = _allOptions.find(o => String(o.value) === String(val));
        options = [...options, legacy || { value: val, label: val }];
      }
      // Exclude options whose value equals another field's value (e.g. manager_id !== person_id
      // — an employee cannot be their own manager).
      if (cfg.excludeIfMatches) {
        const excludeVal = formData[cfg.excludeIfMatches];
        if (excludeVal) {
          options = options.filter(o => String(o.value) !== String(excludeVal));
        }
      }
      const _cascadeDisabled = !!_parentField && !_parentVal;
      const _isReadOnly = !!cfg.readOnly || (!!record && !!cfg.editReadonly) || isAutoFilledLocked;
      const _disabled = _cascadeDisabled || _isReadOnly;
      const _parentLabel = _parentField ? (tableConfig?.fields[_parentField]?.label || _parentField) : '';

      if (_isReadOnly) {
        const displayLabel = options.find(o => String(o.value) === String(val))?.label || val || '';
        const hasValue = !!displayLabel;
        const autoFilledMsg = autoFilledBy ? `Auto-populated from ${autoFilledBy.label}` : '';
        return (
          <TextField fullWidth size="small" label={label} value={displayLabel} required={req} readOnly
            error={!!err}
            helperText={err || autoFilledMsg || resolvedHint || (_cascadeDisabled ? `Select ${_parentLabel} first` : '\u00A0')}
            placeholder={!formData.COMPANY_id && field === 'ldg_id' ? 'Select a company first' : ''}
            InputProps={{
              readOnly: true,
              sx: {
                fontSize: 13, borderRadius: '8px', bgcolor: hasValue ? '#F8FAFC' : '#FAFBFC',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: hasValue ? '#E2E8F0' : '#E2E8F0' }
              },
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ fontSize: 14, color: hasValue ? '#64748B' : '#CBD5E1' }} />
                </InputAdornment>
              ),
            }}
            InputLabelProps={{ shrink: hasValue || !!formData.COMPANY_id || undefined, sx: { fontSize: 13 } }}
            FormHelperTextProps={{ sx: { fontSize: 11 } }}
          />
        );
      }

      return (
        <FormControl fullWidth size="small" error={!!err} required={req} disabled={isFormLocked || _disabled}>
          <InputLabel sx={{ fontSize: 13 }}>{label}</InputLabel>
          <Select
            value={val} label={label}
            disabled={isFormLocked || _disabled}
            onChange={e => handleChange(field, e.target.value)}
            sx={{ fontSize: 13, borderRadius: '8px' }}
            startAdornment={loading ? <CircularProgress size={13} sx={{ mr: 1 }} /> : null}
          >
            {!req && <MenuItem value=""><em style={{ color: '#94A3B8' }}>— Select —</em></MenuItem>}
            {loading && <MenuItem disabled><em>Loading…</em></MenuItem>}
            {!loading && _cascadeDisabled && <MenuItem disabled><em>Select {_parentLabel} first</em></MenuItem>}
            {!loading && !_cascadeDisabled && options.length === 0 && <MenuItem disabled><em>{cfg.emptyMsg || 'No records found'}</em></MenuItem>}
            {!loading && !_cascadeDisabled && options.map(opt => {
              const badge = cfg.badgeFn && opt._row ? cfg.badgeFn(opt._row, formData) : null;
              return (
                <MenuItem key={opt.value} value={opt.value} sx={{
                  fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  bgcolor: badge ? '#F5F9FF !important' : 'transparent',
                  borderLeft: badge ? '4px solid #2196F3' : 'none',
                  fontWeight: badge ? 600 : 'normal',
                  '&:hover': {
                    bgcolor: badge ? '#EBF3FE !important' : undefined
                  }
                }}>
                  <Box>
                    <Typography variant="body2" fontSize={13} lineHeight={1.3} sx={{ fontWeight: badge ? 600 : 'normal' }}>{opt.label}</Typography>
                    {opt.label !== opt.value && (
                      <Typography variant="caption" color="text.secondary" fontSize={10}>{opt.value}</Typography>
                    )}
                  </Box>
                  {badge && (
                    <Chip
                      label={badge}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: 10,
                        fontWeight: 700,
                        bgcolor: '#E3F2FD',
                        color: '#1565C0',
                        border: '1px solid #BBDEFB',
                        ml: 1
                      }}
                    />
                  )}
                </MenuItem>
              );
            })}
          </Select>
          <FormHelperText sx={{ fontSize: 11, color: (!loading && !_cascadeDisabled && options.length === 0) ? '#d32f2f' : undefined }}>
            {err || (!loading && !_cascadeDisabled && options.length === 0 ? (cfg.emptyMsg || 'No records found') : (autoFilledBy && !isAutoFilledLocked ? `Auto-populated from ${autoFilledBy.label}` : '')) || (_cascadeDisabled ? `Select ${_parentLabel} first to filter options` : (resolvedHint || ' '))}
          </FormHelperText>
        </FormControl>
      );
    }

    // ── Custom loan/advance recoveries repeatable table ───────────────────
    if (cfg?.type === 'custom-recoveries') {
      const lines = Array.isArray(val) ? val : (typeof val === 'string' ? JSON.parse(val || '[]') : []);
      const handleLineChange = (index, recoveryAmt) => {
        const newLines = [...lines];
        newLines[index] = {
          ...newLines[index],
          recovery_amount: Number(recoveryAmt) || 0
        };
        handleChange(field, newLines);
      };

      return (
        <Box sx={{ border: '1px solid #CBD5E1', borderRadius: '8px', p: 2, bgcolor: '#F8FAFC', width: '100%' }}>
          <Typography fontSize={13} fontWeight={700} color="#475569" mb={1.5}>
            {label}
          </Typography>
          {lines.length === 0 ? (
            <Typography fontSize={12} color="#94A3B8" fontStyle="italic">
              No active loans or advances found for this assignment.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontSize: 11, fontWeight: 700, color: '#475569', padding: '6px' }}>Loan ID / Policy Type</TableCell>
                  <TableCell align="right" style={{ fontSize: 11, fontWeight: 700, color: '#475569', padding: '6px' }}>Outstanding (₹)</TableCell>
                  <TableCell align="right" style={{ fontSize: 11, fontWeight: 700, color: '#475569', padding: '6px' }} width={160}>Recovery Amount (₹)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line, idx) => (
                  <TableRow key={line.loan_id}>
                    <TableCell style={{ fontSize: 12, padding: '6px' }}>
                      <Typography fontSize={12} fontWeight={600} color="#1E293B">
                        {line.loan_id}
                      </Typography>
                      <Typography fontSize={10} color="#64748B">
                        {line.loan_type}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" style={{ fontSize: 12, fontWeight: 600, padding: '6px' }}>
                      {(line.outstanding || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell align="right" style={{ padding: '6px' }}>
                      <TextField
                        size="small"
                        type="number"
                        disabled={isFormLocked}
                        value={line.recovery_amount ?? ''}
                        onChange={(e) => handleLineChange(idx, e.target.value)}
                        inputProps={{ min: 0, max: line.outstanding, style: { textAlign: 'right', fontSize: 12, padding: '6px 10px' } }}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      );
    }

    // ── Month dropdown ────────────────────────────────────────────────────
    if (cfg?.type === 'month') {
      return (
        <FormControl fullWidth size="small" error={!!err} required={req} disabled={isFormLocked}>
          <InputLabel sx={{ fontSize: 13 }}>{label}</InputLabel>
          <Select value={val} label={label} onChange={e => handleChange(field, e.target.value)}
            disabled={isFormLocked}
            sx={{ fontSize: 13, borderRadius: '8px' }}>
            <MenuItem value=""><em style={{ color: '#94A3B8' }}>— Select month —</em></MenuItem>
            {MONTHS.map(m => <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>{m}</MenuItem>)}
          </Select>
          <FormHelperText sx={{ fontSize: 11 }}>{err || '\u00A0'}</FormHelperText>
        </FormControl>
      );
    }

    // ── Flag toggle switch ────────────────────────────────────────────────
    if (cfg?.type === 'flag') {
      const isOn = val === 'Y';
      return (
        <Box
          role="button" tabIndex={isFormLocked ? -1 : 0}
          aria-label={`${label}: ${isOn ? 'Yes' : 'No'}`}
          onClick={isFormLocked ? undefined : () => handleChange(field, isOn ? 'N' : 'Y')}
          onKeyDown={isFormLocked ? undefined : e => (e.key === ' ' || e.key === 'Enter') && handleChange(field, isOn ? 'N' : 'Y')}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2, py: 1.25, borderRadius: '8px', border: '1.5px solid',
            borderColor: err ? '#EF4444' : isOn ? alpha('#10B981', 0.45) : '#E2E8F0',
            bgcolor: isOn ? alpha('#10B981', 0.06) : 'white',
            cursor: isFormLocked ? 'default' : 'pointer', transition: 'all 0.15s ease',
            userSelect: 'none', outline: 'none', minHeight: 40,
            '&:hover': { borderColor: isOn ? alpha('#10B981', 0.65) : '#CBD5E1', bgcolor: isOn ? alpha('#10B981', 0.1) : '#F8FAFC' },
            ...(!isFormLocked && { '&:focus-visible': { boxShadow: `0 0 0 3px ${alpha('#22c55e', 0.25)}` } }),
          }}
        >
          <Typography fontSize={13} fontWeight={500} color={err ? '#EF4444' : isOn ? '#065F46' : '#475569'}>
            {label}{req && <span style={{ color: '#EF4444', marginLeft: 3 }}>*</span>}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pointerEvents: 'none' }}>
            <Typography fontSize={11.5} fontWeight={700} color={isOn ? '#059669' : '#94A3B8'}>
              {isOn ? 'Yes' : 'No'}
            </Typography>
            <Switch checked={isOn} size="small" tabIndex={-1} disabled={isFormLocked} sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#10B981' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#10B981' },
            }} />
          </Box>
        </Box>
      );
    }

    // ── Time picker ───────────────────────────────────────────────────────
    if (cfg?.type === 'time' || field === 'start_time' || field === 'end_time') {
      return (
        <TextField fullWidth size="small" type="time" required={req || field === 'start_time' || field === 'end_time'} label={label} value={val}
          onChange={e => handleChange(field, e.target.value)}
          error={!!err} helperText={err || (autoFilledBy && !isAutoFilledLocked ? `Auto-populated from ${autoFilledBy.label}` : '') || (resolvedHint || '\u00A0')}
          disabled={isFormLocked || isAutoFilledLocked}
          InputLabelProps={{ shrink: true, sx: { fontSize: 13 } }}
          InputProps={{ sx: { fontSize: 13, borderRadius: '8px' } }}
          FormHelperTextProps={{ sx: { fontSize: 11 } }}
        />
      );
    }

    // ── Date picker ───────────────────────────────────────────────────────
    if (cfg?.type === 'date' || (!cfg && isLegacyDate(field))) {
      // If `afterField` is set, also pass `min` to the HTML date input so the
      // calendar visually disables dates on/before the referenced field's value.
      const minDate = cfg?.afterField ? formData[cfg.afterField] : undefined;
      const maxDate = cfg?.noFuture
        ? new Date().toISOString().slice(0, 10)
        : (cfg?.maxDate || undefined);
      const dateInputProps = {};
      if (minDate) dateInputProps.min = minDate;
      if (maxDate) dateInputProps.max = maxDate;
      return (
        <TextField fullWidth size="small" type="date" required={req} label={label} value={val}
          onChange={e => handleChange(field, e.target.value)}
          error={!!err} helperText={err || (autoFilledBy && !isAutoFilledLocked ? `Auto-populated from ${autoFilledBy.label}` : '') || (resolvedHint || '\u00A0')}
          disabled={isFormLocked || isAutoFilledLocked}
          inputProps={{ ...dateInputProps, readOnly: isFormLocked }}
          InputLabelProps={{ shrink: true, sx: { fontSize: 13 } }}
          InputProps={{ sx: { fontSize: 13, borderRadius: '8px' } }}
          FormHelperTextProps={{ sx: { fontSize: 11 } }}
        />
      );
    }

    // ── URL input ─────────────────────────────────────────────────────────
    if (cfg?.type === 'url') {
      const charCount = String(val).length;
      const maxL = cfg.maxLength;
      const hint = charCount > 0 ? `${charCount} / ${maxL}` : 'e.g. https://company.com';
      return (
        <TextField fullWidth size="small" type="url" required={req} label={label} value={val}
          onChange={e => handleChange(field, e.target.value)}
          disabled={isFormLocked}
          error={!!err} helperText={err || hint}
          placeholder="https://example.com"
          inputProps={{ maxLength: maxL, readOnly: isFormLocked }}
          InputProps={{
            sx: { fontSize: 13, borderRadius: '8px' },
            startAdornment: (
              <InputAdornment position="start">
                <LanguageIcon sx={{ fontSize: 15, color: err ? '#EF4444' : '#94A3B8' }} />
              </InputAdornment>
            ),
          }}
          InputLabelProps={{ sx: { fontSize: 13 } }}
          FormHelperTextProps={{ sx: { fontSize: 11, display: 'flex', justifyContent: charCount > 0 && !err ? 'flex-end' : 'flex-start' } }}
        />
      );
    }

    // ── File or URL input ─────────────────────────────────────────────────
    if (cfg?.type === 'file-or-url') {
      const isDataUrl = typeof val === 'string' && val.startsWith('data:');
      const inputId = `file-url-${field}`;
      return (
        <Box>
          <Typography component="label" sx={{ fontSize: 12, fontWeight: 600, color: err ? '#EF4444' : '#475569', display: 'block', mb: 0.75 }}>
            {label}{req && <span style={{ color: '#EF4444', marginLeft: 3 }}>*</span>}
          </Typography>
          {isDataUrl ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid #E2E8F0', borderRadius: '8px', bgcolor: '#F8FAFC' }}>
              <CloudUploadIcon sx={{ color: '#10B981', fontSize: 20 }} />
              <Typography fontSize={13} sx={{ flex: 1, color: '#334155' }} noWrap>
                File attached ({(val.length / 1024).toFixed(1)} KB)
              </Typography>
              {!isFormLocked && (
                <IconButton size="small" onClick={() => handleChange(field, '')} sx={{ color: '#EF4444' }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField fullWidth size="small" type="url" value={val || ''}
                onChange={e => handleChange(field, e.target.value)}
                disabled={isFormLocked}
                error={!!err} helperText={err || resolvedHint || 'Enter URL or upload a file'}
                placeholder="https://..."
                InputProps={{
                  sx: { fontSize: 13, borderRadius: '8px' },
                  startAdornment: <InputAdornment position="start"><LanguageIcon sx={{ fontSize: 15, color: '#94A3B8' }} /></InputAdornment>,
                }}
                FormHelperTextProps={{ sx: { fontSize: 11 } }}
              />
              {!isFormLocked && (
                <Box>
                  <Button variant="outlined" component="label" htmlFor={inputId} sx={{ height: 40, minWidth: 'auto', px: 2, borderRadius: '8px', borderColor: '#E2E8F0', color: '#64748B', '&:hover': { borderColor: '#10B981', color: '#10B981', bgcolor: '#F0FDF4' } }}>
                    <CloudUploadIcon sx={{ fontSize: 18 }} />
                  </Button>
                  <input id={inputId} type="file" accept={cfg.accept || '*/*'} hidden disabled={isFormLocked}
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = ev => handleChange(field, ev.target.result);
                      reader.readAsDataURL(file);
                      e.target.value = '';
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      );
    }

    // ── Image upload with preview ─────────────────────────────────────────
    if (cfg?.type === 'image') {
      const previewUrl = val && String(val).startsWith('data:') ? val : null;
      const inputId = `img-upload-${field}`;
      return (
        <Box>
          <Typography component="label" htmlFor={isFormLocked ? undefined : inputId}
            sx={{ fontSize: 12, fontWeight: 600, color: err ? '#EF4444' : '#475569', display: 'block', mb: 0.75, cursor: isFormLocked ? 'default' : 'pointer' }}>
            {label}{req && <span style={{ color: '#EF4444', marginLeft: 3 }}>*</span>}
          </Typography>
          <Box component="label" htmlFor={isFormLocked ? undefined : inputId}
            sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 0.75, p: previewUrl ? 1 : 2.5,
              borderRadius: '10px', border: `2px dashed ${err ? '#EF4444' : '#CBD5E1'}`,
              bgcolor: previewUrl ? 'transparent' : '#F8FAFC',
              cursor: isFormLocked ? 'default' : 'pointer', minHeight: previewUrl ? 'auto' : 110,
              transition: 'all 0.15s ease',
              ...(!isFormLocked && { '&:hover': { borderColor: '#22c55e', bgcolor: previewUrl ? alpha('#22c55e', 0.03) : '#f0fdf4' } }),
            }}
          >
            {previewUrl
              ? <Box component="img" src={previewUrl} alt={label}
                sx={{ maxHeight: 130, maxWidth: '100%', borderRadius: '8px', objectFit: 'contain', display: 'block' }} />
              : <>
                <CloudUploadIcon sx={{ fontSize: 30, color: '#94A3B8' }} />
                <Typography fontSize={12.5} fontWeight={500} color="text.secondary">Click to upload</Typography>
                <Typography fontSize={10.5} color="#94A3B8">{resolvedHint || 'PNG, JPG up to 5 MB'}</Typography>
              </>
            }
            <input id={inputId} type="file" accept={cfg.accept || 'image/*'} hidden disabled={isFormLocked}
              onChange={e => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = ev => handleChange(field, ev.target.result);
                reader.readAsDataURL(file);
                e.target.value = '';
              }}
            />
          </Box>
          {previewUrl && !isFormLocked && (
            <Button size="small" startIcon={<DeleteOutlineIcon sx={{ fontSize: 13 }} />}
              onClick={() => handleChange(field, '')}
              sx={{
                mt: 0.5, fontSize: 11, color: '#94A3B8', px: 1,
                '&:hover': { color: '#EF4444', bgcolor: alpha('#EF4444', 0.06) }
              }}>
              Remove
            </Button>
          )}
          {err && <Typography fontSize={11} color="error.main" mt={0.5}>{err}</Typography>}
        </Box>
      );
    }

    // ── Read-only (auto-filled) text ──────────────────────────────────────
    if (cfg?.readOnly) {
      const hasValue = !!val;
      const isEligibility = field === 'eligibility_status';
      
      let bg = hasValue ? '#F0FDF4' : '#F8FAFC';
      let border = hasValue ? alpha('#10B981', 0.4) : '#E2E8F0';
      let iconColor = hasValue ? '#059669' : '#CBD5E1';
      let textColor = undefined;
      
      if (isEligibility) {
        if (val === 'ELIGIBLE') {
          bg = '#dcfce7'; border = '#bbf7d0'; iconColor = '#15803d'; textColor = '#15803d';
        } else if (val === 'NOT_ELIGIBLE') {
          bg = '#fef2f2'; border = '#fecaca'; iconColor = '#991b1b'; textColor = '#991b1b';
        } else if (val === 'CONDITIONAL') {
          bg = '#fef3c7'; border = '#fde68a'; iconColor = '#92400e'; textColor = '#92400e';
        }
      }

      // If it's a select field, display the label instead of raw value
      let displayVal = val;
      if (cfg?.type === 'select' && Array.isArray(cfg.options)) {
        const opt = cfg.options.find(o => (typeof o === 'object' ? o.value : o) === val);
        if (opt) displayVal = typeof opt === 'object' ? opt.label : opt;
      }

      return (
        <TextField fullWidth size="small" label={label} value={displayVal}
          helperText={resolvedHint || (hasValue ? '\u00A0' : 'Auto-filled')}
          InputProps={{
            readOnly: true,
            sx: {
              fontSize: 13, borderRadius: '8px', bgcolor: bg,
              ...(textColor ? { color: textColor, fontWeight: 700, '& input': { color: textColor, WebkitTextFillColor: textColor } } : {}),
              '& .MuiOutlinedInput-notchedOutline': { borderColor: border }
            },
            endAdornment: (
              <InputAdornment position="end">
                <LockOutlinedIcon sx={{ fontSize: 14, color: iconColor }} />
              </InputAdornment>
            ),
          }}
          InputLabelProps={{ shrink: hasValue || undefined, sx: { fontSize: 13 } }}
          FormHelperTextProps={{ sx: { fontSize: 11, fontStyle: 'italic', color: '#94A3B8' } }}
        />
      );
    }

    // ── Number ────────────────────────────────────────────────────────────
    if (cfg?.type === 'number' || (!cfg && isLegacyNum(field))) {
      const baseHint = resolvedHint;
      const minMaxHint = cfg
        ? [cfg.min !== undefined && `Min: ${cfg.min}`, cfg.max !== undefined && `Max: ${cfg.max}`].filter(Boolean).join('  ')
        : '';
      const hint = [baseHint, minMaxHint].filter(Boolean).join(' | ') || '\u00A0';
      return (
        <TextField fullWidth size="small" type="number" required={req} label={label} value={val}
          onChange={e => handleChange(field, e.target.value)}
          error={!!err} helperText={err || (autoFilledBy && !isAutoFilledLocked ? `Auto-populated from ${autoFilledBy.label}` : '') || hint}
          disabled={isFormLocked || !!cfg?.readOnly || (!!record && !!cfg?.editReadonly) || isAutoFilledLocked}
          inputProps={{ min: cfg?.min, max: cfg?.max, step: cfg?.integerOnly ? 1 : 'any', readOnly: isFormLocked || !!cfg?.readOnly || (!!record && !!cfg?.editReadonly) || isAutoFilledLocked }}
          InputProps={{ sx: { fontSize: 13, borderRadius: '8px' } }}
          InputLabelProps={{ sx: { fontSize: 13 } }}
          FormHelperTextProps={{ sx: { fontSize: 11 } }}
        />
      );
    }

    // ── Text (default) ────────────────────────────────────────────────────
    const charCount = String(val).length;
    const maxL = cfg?.maxLength;
    // -- Select (static options) -- short-circuits BEFORE the text fallback
    if (cfg?.type === 'select' && Array.isArray(cfg.options)) {
      let opts = cfg.options.map(opt => ({
        value: typeof opt === 'object' ? opt.value : opt,
        label: typeof opt === 'object' ? opt.label : opt
      }));

      // Ensure the saved value is in the options list so it renders correctly
      if (val !== undefined && val !== null && val !== '' && !opts.some(o => String(o.value) === String(val))) {
        opts = [...opts, { value: val, label: val }];
      }

      const isEligibility = field === 'eligibility_status';
      let selectSx = { fontSize: 13, borderRadius: '8px' };
      if (isEligibility) {
        if (val === 'ELIGIBLE') {
          selectSx = { ...selectSx, bgcolor: '#dcfce7', color: '#15803d', fontWeight: 600, '& fieldset': { borderColor: '#bbf7d0' } };
        } else if (val === 'NOT_ELIGIBLE') {
          selectSx = { ...selectSx, bgcolor: '#fef2f2', color: '#991b1b', fontWeight: 600, '& fieldset': { borderColor: '#fecaca' } };
        } else if (val === 'CONDITIONAL') {
          selectSx = { ...selectSx, bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, '& fieldset': { borderColor: '#fde68a' } };
        }
      }

      return (
        <FormControl fullWidth size="small" error={!!err} required={req} disabled={isFormLocked || !!cfg.readOnly || (!!record && !!cfg.editReadonly)}>
          <InputLabel sx={{ fontSize: 13 }}>{label}</InputLabel>
          <Select value={val} label={label}
            disabled={isFormLocked || !!cfg.readOnly || (!!record && !!cfg.editReadonly)}
            onChange={e => handleChange(field, e.target.value)}
            sx={selectSx}>
            {!req && <MenuItem value=""><em style={{ color: '#94A3B8' }}>-- Select --</em></MenuItem>}
            {opts.map(opt => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>{opt.label}</MenuItem>
            ))}
          </Select>
          <FormHelperText sx={{ fontSize: 11 }}>{err || resolvedHint || '\u00a0'}</FormHelperText>
        </FormControl>
      );
    }

    const counterHint = maxL && charCount > 0 ? `${charCount} / ${maxL}` : (cfg?.patternMsg || '\u00A0');
    const helperAlign = maxL && charCount > 0 && !err ? 'flex-end' : 'flex-start';

    return (
      <TextField fullWidth size="small" required={req} label={label} value={val}
        onChange={e => handleChange(field, e.target.value)}
        error={!!err} helperText={err || (autoFilledBy && !isAutoFilledLocked ? `Auto-populated from ${autoFilledBy.label}` : '') || counterHint}
        disabled={isFormLocked || !!cfg?.readOnly || (!!record && !!cfg?.editReadonly) || isAutoFilledLocked}
        inputProps={{ maxLength: maxL, readOnly: isFormLocked }}
        type={cfg?.type === 'email' || (!cfg && isLegacyEmail(field)) ? 'email' : 'text'}
        InputProps={{ sx: { fontSize: 13, borderRadius: '8px' } }}
        InputLabelProps={{ sx: { fontSize: 13 } }}
        FormHelperTextProps={{ sx: { fontSize: 11, display: 'flex', justifyContent: helperAlign } }}
      />
    );
  };

  // ── Sections-based layout ─────────────────────────────────────────────────
  const renderSections = () => {
    const { sections } = tableConfig;
    // Filter out edit-only sections when creating a new record
    const visibleSections = sections.filter(s => !s.editOnly || record);
    const sectionedFields = new Set(visibleSections.flatMap(s => s.fields));
    const uncategorized = visibleFields.filter(f => !sectionedFields.has(f));

    const isVisible = f => isFieldVisible(f);

    return (
      <Stack spacing={2.5}>
        {visibleSections.map(section => {
          const sectionFields = section.fields.filter(isVisible);
          if (!sectionFields.length) return null;
          const smCols = Math.floor(12 / (section.columns || 2));
          return (
            <FormSection key={section.id} sectionId={section.id} label={section.label}>
              <Grid container spacing={2}>
                {sectionFields.map(f => {
                  const fType = tableConfig.fields[f]?.type;
                  // auto-code preview is always full-width regardless of section columns
                  const fullWidth = fType === 'auto-code';
                  return (
                    <Grid item xs={12} sm={fullWidth ? 12 : smCols} key={f}>
                      {renderField(f)}
                    </Grid>
                  );
                })}
              </Grid>
            </FormSection>
          );
        })}
        {uncategorized.length > 0 && (
          <FormSection sectionId="details" label="Other Details">
            <Grid container spacing={2}>
              {uncategorized.map(f => (
                <Grid item xs={12} sm={6} key={f}>{renderField(f)}</Grid>
              ))}
            </Grid>
          </FormSection>
        )}
      </Stack>
    );
  };

  // ── Auto-categorized layout (fallback) ───────────────────────────────────
  const renderAutoLayout = () => (
    <Stack spacing={2.5}>
      {dropdownFields.length > 0 && (
        <FormSection sectionId="references" label="References">
          <Grid container spacing={2}>
            {dropdownFields.map(f => <Grid item xs={12} sm={6} key={f}>{renderField(f)}</Grid>)}
          </Grid>
        </FormSection>
      )}
      {otherFields.length > 0 && (
        <FormSection sectionId="details" label="Details">
          <Grid container spacing={2}>
            {otherFields.map(f => <Grid item xs={12} sm={6} key={f}>{renderField(f)}</Grid>)}
          </Grid>
        </FormSection>
      )}
      {dateFields.length > 0 && (
        <FormSection sectionId="dates" label="Dates">
          <Grid container spacing={2}>
            {dateFields.map(f => <Grid item xs={12} sm={6} key={f}>{renderField(f)}</Grid>)}
          </Grid>
        </FormSection>
      )}
      {flagFields.length > 0 && (
        <FormSection sectionId="status" label="Status">
          <Grid container spacing={2}>
            {flagFields.map(f => <Grid item xs={12} sm={4} key={f}>{renderField(f)}</Grid>)}
          </Grid>
        </FormSection>
      )}
    </Stack>
  );

  // ── Dialog ────────────────────────────────────────────────────────────────
  return (
    <>
      <Dialog
        open={open}
        onClose={isSubmitting ? undefined : onClose}
        maxWidth="md" fullWidth scroll="paper"
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        {/* Header */}
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
            px: 3, py: 2.5,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Stack direction="row" alignItems="center" gap={2}>
              <Avatar sx={{
                width: 44, height: 44, borderRadius: '12px',
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(4px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}>
                {record ? <EditNoteIcon sx={{ fontSize: 22 }} /> : <AddIcon sx={{ fontSize: 22 }} />}
              </Avatar>
              <Box>
                <Typography fontWeight={800} fontSize={16} color="white" lineHeight={1.2}>
                  {isFormLocked ? 'View Settlement' : record ? 'Edit Record' : 'New Record'}
                </Typography>
                <Stack direction="row" alignItems="center" gap={1} mt={0.4}>
                  <Typography fontSize={12} sx={{ color: 'rgba(255,255,255,0.75)' }}>
                    {tableLabel(tableName)}
                  </Typography>
                  {isFormLocked ? (
                    <Chip label="LOCKED" size="small" icon={<LockOutlinedIcon style={{ color: 'white', fontSize: 11 }} />} sx={{
                      height: 18, fontSize: 9, fontWeight: 700, letterSpacing: 0.3,
                      bgcolor: '#dc2626', color: 'white', '& .MuiChip-icon': { color: 'white' }
                    }} />
                  ) : !record ? (
                    <Chip label="ID auto-generated" size="small" sx={{
                      height: 16, fontSize: 9, fontWeight: 700, letterSpacing: 0.3,
                      bgcolor: 'rgba(255,255,255,0.2)', color: 'white',
                    }} />
                  ) : null}
                </Stack>
              </Box>
            </Stack>
            <IconButton onClick={onClose} disabled={isSubmitting} sx={{
              color: 'white', bgcolor: 'rgba(255,255,255,0.15)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
            }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Error banner */}
          {errorCount > 0 && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              bgcolor: '#FEF2F2', borderBottom: '1px solid #FECACA', px: 3, py: 1,
            }}>
              <ErrorOutlineIcon sx={{ fontSize: 16, color: '#EF4444' }} />
              <Typography fontSize={12.5} color="error.main" fontWeight={600}>
                {errorCount} field{errorCount > 1 ? 's require' : ' requires'} attention before saving
              </Typography>
            </Box>
          )}
        </DialogTitle>

        {/* Body */}
        <DialogContent sx={{ px: 3, py: 2.5, bgcolor: '#F8FAFC' }}>
          {/* Form-level notice banner — shown for tables that have one configured */}
          {tableConfig?.formNotice && !record && (
            <Alert
              severity={tableConfig.formNotice.severity || 'info'}
              sx={{
                mb: 2, borderRadius: '10px',
                '& .MuiAlert-message': { fontSize: 12.5 }
              }}
            >
              {tableConfig.formNotice.title && (
                <Typography fontWeight={700} fontSize={13} sx={{ mb: 0.3 }}>
                  {tableConfig.formNotice.title}
                </Typography>
              )}
              {tableConfig.formNotice.message}
            </Alert>
          )}

          {/* Pending Update Alert for PF/ESI */}
          {(tableName === 'PF_ACCOUNT' || tableName === 'ESI_ACCOUNT') && formData?.pending_update_date && formData.pending_update_date > new Date().toISOString().split('T')[0] && (
            <Alert
              severity="warning"
              sx={{
                mb: 2, borderRadius: '10px',
                '& .MuiAlert-message': { fontSize: 13, fontWeight: 500 }
              }}
            >
              Assignment ID: {formData?.assignment_id} – {tableName === 'PF_ACCOUNT' ? 'PF' : 'ESI'} contribution and related values will be updated from the Effective Date: {formData.pending_update_date.split('-').reverse().join('-')}.
            </Alert>
          )}

          {tableConfig?.sections ? renderSections() : renderAutoLayout()}
        </DialogContent>

        {/* Footer */}
        <DialogActions sx={{
          px: 3, py: 2, bgcolor: 'white',
          borderTop: '1px solid #E2E8F0',
          justifyContent: 'space-between',
        }}>
          <Typography fontSize={11.5} color="text.disabled">
            Fields marked <span style={{ color: '#EF4444' }}>*</span> are required
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Button
              onClick={onClose} disabled={isSubmitting} variant="outlined"
              sx={{
                borderColor: '#E2E8F0', color: '#64748B', px: 2.5,
                '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' }
              }}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit} disabled={isSubmitting || isFormLocked} variant="contained"
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              sx={{
                px: 3, fontWeight: 700, minWidth: 148, borderRadius: '10px',
                background: 'linear-gradient(135deg,#16a34a,#22c55e)',
                boxShadow: '0 4px 14px rgba(34,197,94,0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg,#15803d,#16a34a)',
                  boxShadow: '0 6px 18px rgba(34,197,94,0.5)',
                },
                '&.Mui-disabled': {
                  background: 'linear-gradient(135deg,#16a34a,#22c55e)',
                  opacity: 0.7, color: 'white',
                },
              }}>
              {isSubmitting ? 'Saving…' : record ? 'Save Changes' : 'Create Record'}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Success toast */}
      <Snackbar
        open={showSuccess} autoHideDuration={2500}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success" variant="filled"
          icon={<CheckCircleIcon fontSize="inherit" />}
          sx={{ minWidth: 280, fontSize: 13, borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}
        >
          {record ? 'Record updated successfully!' : 'Record created successfully!'}
        </Alert>
      </Snackbar>
    </>
  );
}

function FormSection({ sectionId, label, children }) {
  const cfg = SECTION_CONFIG[sectionId] || SECTION_CONFIG.details;
  return (
    <Box sx={{
      bgcolor: 'white', borderRadius: '14px',
      border: '1.5px solid #E8EDF5',
      p: 2.5,
    }}>
      {/* Section header: icon pill + label + divider line */}
      <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 2 }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: '8px',
          bgcolor: alpha(cfg.color, 0.1),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {React.cloneElement(cfg.icon, { sx: { fontSize: 15, color: cfg.color } })}
        </Box>
        <Typography fontSize={12} fontWeight={700} letterSpacing={0.6}
          sx={{ color: 'text.secondary', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: '#E8EDF5' }} />
      </Stack>
      {children}
    </Box>
  );
}
